/* ============================================================
   custom.js — Backdropsource global custom JavaScript
   All section-level JS lives here, initialised on DOMContentLoaded.
   Each init function is guarded so it only runs when its root
   element is present on the page.
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {
  bdHeroShowcaseInit();
  bdHeroCornerVideoInit();
  bdTestimonialVideoInit();
  bdProductSliderInit();
  document.querySelectorAll('.booth-showcase').forEach(section => {
    initBoothShowcase(section);
  });
   bdUseCasesInit();
   bdOrderShowcaseInit();
   bdCategoryBentoInit();
  bdValuesInit();
  bdStatsInit();
  bdCollectionGalleryInit();
  productSpecsTabsInit();
  productFaqInit();
  pdartInit();
  pfabInit();
  headerPromoInit();
  bdSearchTriggersInit();
  bdSearchDropdownInit();
  megaMenuSpotlightInit()
  // bdsCalendlyTrackingInit();
});

/* ──────────────────────────────────────────────
   Header search → predictive search popup (phones)
   The blue bar's search icon opens the theme's predictive search popup (#search-modal,
   snippets/search-modal.liquid) under the header instead of going straight to /search. Without
   JS, or before the popup's module has loaded, it still goes to /search. Desktop's search bar
   has its own dropdown (bdSearchDropdownInit).
   ────────────────────────────────────────────── */
function bdSearchTriggersInit() {
  const MOBILE_ICON = '#header-group .header-utility-bar__search';
  const modal = document.getElementById('search-modal');

  // Phones open the popup under the header, like the menu drawer (custom.css reads --bd-search-top)
  const setPanelTop = () => {
    const header = document.querySelector('header-component');
    if (!modal || !header) return;
    modal.style.setProperty('--bd-search-top', `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`);
  };

  const isOpen = () => !!modal?.querySelector('dialog[open]');
  let closedByHeaderTap = 0;

  if (modal) {
    // Also covers the theme's own openers (e.g. its keyboard shortcut)
    modal.addEventListener('dialog:open', setPanelTop);
    window.addEventListener('resize', () => { if (isOpen()) setPanelTop(); });

    // Phones: the header stays visible above the panel but is inert while it's open, so a tap on it
    // (the search icon again included) closes the panel, like the menu toggle
    window.addEventListener('pointerdown', (event) => {
      const dialog = modal.querySelector('dialog[open]');
      if (!dialog || !(event.target instanceof Node) || dialog.contains(event.target)) return;
      closedByHeaderTap = Date.now();
      if (typeof modal.closeDialog === 'function') modal.closeDialog();
    }, true);
  }

  const openSearch = (text = '') => {
    if (!modal || typeof modal.showDialog !== 'function') return false;

    setPanelTop();
    modal.showDialog();

    // showDialog() opens on the next frame; focus and search once it is open
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const input = modal.querySelector('input[type="search"]');
      if (!input) return;

      input.focus();
      if (text) {
        input.value = text;
        input.setSelectionRange(text.length, text.length);
        input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: text }));
      }
    }));

    return true;
  };

  // Delegated so a re-rendered header keeps working
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const icon = target.closest(MOBILE_ICON);
    if (icon) {
      // The same tap just closed the panel; don't reopen it
      if (Date.now() - closedByHeaderTap < 600) event.preventDefault();
      else if (openSearch()) event.preventDefault();
    }
  });
}

/* ──────────────────────────────────────────────
   Header search dropdown (desktop)
   The logo row's search bar opens snippets/bd-search-dropdown.liquid right under it. Before
   typing: recent searches (kept in this browser), popular searches, categories and products.
   While typing: suggestions, collections and products from the predictive search API, rendered
   by sections/bd-search-results.liquid. Enter still goes to the full /search page.
   ────────────────────────────────────────────── */
function bdSearchDropdownInit() {
  const wrap = document.querySelector('#header-group .header-inline-search');
  const dropdown = wrap?.querySelector('[data-bd-sd]');
  const input = wrap?.querySelector('.search-page-input');
  const form = input?.form;
  const template = wrap?.querySelector('template[data-bd-sd-recent-template]');
  if (!wrap || !dropdown || !(input instanceof HTMLInputElement) || !form || !(template instanceof HTMLTemplateElement)) return;

  const desktop = window.matchMedia('(min-width: 750px)');
  const defaultPanel = dropdown.querySelector('[data-bd-sd-default]');
  const results = dropdown.querySelector('[data-bd-sd-results]');
  const recentGroup = dropdown.querySelector('[data-bd-sd-recent]');
  const recentList = dropdown.querySelector('[data-bd-sd-recent-list]');
  const closeButton = wrap.querySelector('[data-bd-sd-close]');
  const searchUrl = form.getAttribute('action') || '/search';
  const STORAGE_KEY = 'bd-recent-searches';
  const MAX_RECENT = 5;
  let controller = null;
  let timer = 0;
  let pointerInside = false;
  let keepClosed = false;

  // Dims the page under the header while open (styled in snippets/bd-search-dropdown.liquid)
  const dim = document.createElement('div');
  dim.className = 'bd-sd-dim';
  dim.hidden = true;
  dim.setAttribute('aria-hidden', 'true');
  document.body.append(dim);

  input.setAttribute('autocomplete', 'off');
  input.setAttribute('aria-controls', dropdown.id);
  input.setAttribute('aria-expanded', 'false');

  // Recent searches live in this browser only; storage can be blocked, so every access is guarded
  const readRecent = () => {
    try {
      const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((term) => typeof term === 'string' && term.trim()) : [];
    } catch (error) {
      return [];
    }
  };
  const writeRecent = (list) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (error) { /* not saved */ }
  };
  const remember = (term) => {
    const text = (term || '').trim();
    if (!text) return;
    const rest = readRecent().filter((item) => item.toLowerCase() !== text.toLowerCase());
    writeRecent([text, ...rest].slice(0, MAX_RECENT));
    renderRecent();
  };

  const renderRecent = () => {
    const list = readRecent();
    if (recentGroup) recentGroup.hidden = list.length === 0;
    if (!recentList) return;
    recentList.replaceChildren(...list.map((term) => {
      const item = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
      const link = item.querySelector('a');
      if (link) {
        link.href = `${searchUrl}?q=${encodeURIComponent(term)}&type=product`;
        link.dataset.bdSdTerm = term;
        const label = link.querySelector('span');
        if (label) label.textContent = term;
      }
      return item;
    }));
  };

  // The panel runs from the search bar's left edge to the end of the header row's content;
  // on narrower screens it uses the row's full width instead
  const place = () => {
    const row = wrap.closest('.header__columns');
    const bar = wrap.getBoundingClientRect();
    let left = bar.left;
    let right = bar.right;
    if (row) {
      const rowBox = row.getBoundingClientRect();
      const rowStyle = getComputedStyle(row);
      right = rowBox.right - (parseFloat(rowStyle.paddingRight) || 0);
      if (window.innerWidth < 1100) left = rowBox.left + (parseFloat(rowStyle.paddingLeft) || 0);
    }
    dropdown.style.setProperty('--bd-sd-left', `${Math.round(left - bar.left)}px`);
    dropdown.style.setProperty('--bd-sd-width', `${Math.round(Math.max(bar.width, right - left))}px`);
    // Never taller than the space left under the bar
    dropdown.style.setProperty('--bd-sd-max-height', `${Math.round(Math.max(240, window.innerHeight - bar.bottom - 24))}px`);
    const header = document.querySelector('header-component');
    if (header) dim.style.setProperty('--bd-sd-dim-top', `${Math.round(header.getBoundingClientRect().bottom)}px`);
    const headerSection = header?.closest('.shopify-section');
    const headerZ = headerSection ? parseInt(getComputedStyle(headerSection).zIndex, 10) : NaN;
    if (!Number.isNaN(headerZ)) dim.style.setProperty('--bd-sd-dim-z', String(headerZ - 1));
  };

  const showState = () => {
    const typed = input.value.trim().length > 0;
    const hasResults = results.childElementCount > 0;
    if (defaultPanel) defaultPanel.hidden = typed && hasResults;
    results.hidden = !(typed && hasResults);
  };

  const isOpen = () => !dropdown.hidden;

  const open = () => {
    if (!desktop.matches || isOpen() || keepClosed) return;
    place();
    renderRecent();
    showState();
    dropdown.hidden = false;
    dim.hidden = false;
    if (closeButton) closeButton.hidden = false;
    wrap.classList.add('is-open');
    input.setAttribute('aria-expanded', 'true');
  };

  const close = () => {
    if (!isOpen()) return;
    dropdown.hidden = true;
    dim.hidden = true;
    if (closeButton) closeButton.hidden = true;
    wrap.classList.remove('is-open');
    input.setAttribute('aria-expanded', 'false');
  };

  const fetchResults = async (query) => {
    controller?.abort();
    controller = new AbortController();
    const params = new URLSearchParams({ q: query, section_id: 'bd-search-results' });
    params.set('resources[type]', 'query,product,collection');
    params.set('resources[limit]', '6');
    params.set('resources[limit_scope]', 'each');
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}search/suggest?${params}`, { signal: controller.signal });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const panel = doc.querySelector('[data-bd-sd-results-panel]');
      // Ignore answers to an older query
      if (!panel || input.value.trim() !== query) return;
      results.replaceChildren(panel);
      showState();
    } catch (error) {
      // Aborted by a newer query, or offline: the "before typing" panel stays up
    }
  };

  input.addEventListener('focus', open);
  input.addEventListener('click', open);

  input.addEventListener('input', () => {
    open();
    clearTimeout(timer);
    const query = input.value.trim();
    if (!query) {
      controller?.abort();
      results.replaceChildren();
      showState();
      return;
    }
    timer = setTimeout(() => fetchResults(query), 200);
  });

  const focusables = () => [...dropdown.querySelectorAll('a[href], button')].filter((el) => el.getClientRects().length > 0);

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
    if (event.key === 'ArrowDown' && isOpen()) {
      event.preventDefault();
      focusables()[0]?.focus();
    }
  });

  dropdown.addEventListener('keydown', (event) => {
    const items = focusables();
    const index = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      items[Math.min(index + 1, items.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (index <= 0) input.focus();
      else items[index - 1]?.focus();
    } else if (event.key === 'Escape') {
      close();
      // Back to the bar without reopening on its focus
      keepClosed = true;
      input.focus();
      keepClosed = false;
    }
  });

  dropdown.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-bd-sd-clear]')) {
      writeRecent([]);
      renderRecent();
      input.focus();
      return;
    }
    // Suggestions, recent and popular searches search right here instead of opening /search
    const termLink = /** @type {HTMLElement | null} */ (target.closest('[data-bd-sd-term]'));
    if (termLink) {
      event.preventDefault();
      const term = termLink.dataset.bdSdTerm || '';
      remember(term);
      input.value = term;
      input.focus();
      dropdown.scrollTop = 0;
      clearTimeout(timer);
      fetchResults(term.trim());
      return;
    }
    const rememberLink = /** @type {HTMLElement | null} */ (target.closest('[data-bd-sd-remember]'));
    if (rememberLink) remember(rememberLink.dataset.bdSdRemember);
  });

  // ✕ closes the dropdown and starts the next search fresh, like the phone popup
  closeButton?.addEventListener('click', () => {
    clearTimeout(timer);
    controller?.abort();
    input.value = '';
    results.replaceChildren();
    showState();
    close();
    input.blur();
  });

  form.addEventListener('submit', () => remember(input.value));

  // The bar's own clear (×) link goes to an empty /search page; here it just clears the field
  wrap.querySelector('.search__reset-button')?.addEventListener('click', (event) => {
    event.preventDefault();
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  });

  // Close on a click outside, or when focus tabs out of the bar and the panel
  wrap.addEventListener('pointerdown', () => { pointerInside = true; });
  wrap.addEventListener('pointerup', () => setTimeout(() => { pointerInside = false; }, 0));
  document.addEventListener('pointerdown', (event) => {
    if (isOpen() && event.target instanceof Node && !wrap.contains(event.target)) close();
    if (!(event.target instanceof Node) || !wrap.contains(event.target)) pointerInside = false;
  });
  wrap.addEventListener('focusout', (event) => {
    if (pointerInside) { pointerInside = false; return; }
    if (event.relatedTarget instanceof Node && wrap.contains(event.relatedTarget)) return;
    close();
  });

  window.addEventListener('resize', () => {
    if (!isOpen()) return;
    if (desktop.matches) place();
    else close();
  });
}

/* ──────────────────────────────────────────────
   Header promo pill — click-to-copy discount code
   ────────────────────────────────────────────── */
function headerPromoInit() {
  document.querySelectorAll('[data-header-promo-copy]').forEach((button) => {
    button.addEventListener('click', () => {
      const code = button.dataset.headerPromoCopy;
      if (!code) return;

      const markCopied = () => {
        button.classList.add('is-copied');
        setTimeout(() => button.classList.remove('is-copied'), 1500);
      };

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(markCopied).catch(() => {});
      } else {
        // Fallback for browsers without the Clipboard API.
        const scratch = document.createElement('textarea');
        scratch.value = code;
        scratch.style.position = 'fixed';
        scratch.style.opacity = '0';
        document.body.appendChild(scratch);
        scratch.select();
        try {
          document.execCommand('copy');
          markCopied();
        } catch (err) {
          // Nothing more we can do — fail silently.
        }
        scratch.remove();
      }
    });
  });
}

/* ──────────────────────────────────────────────
   Mega menu "spotlight" layout — sidebar item hover/focus swaps the
   active tinted panel. Explicit JS state (an .is-active class) rather
   than pure CSS :hover/:has(), since the whole submenu's own open/close
   already relies on a :has([aria-expanded]) rule in
   blocks/_header-menu.liquid — stacking a second :has(:hover) rule for
   the panel switch inside that fought with it and caused hovering any
   sidebar item to flicker back to the first one.
   ────────────────────────────────────────────── */
function megaMenuSpotlightInit() {
  document.querySelectorAll('[data-mega-menu-spotlight]').forEach((root) => {
    const items = root.querySelectorAll('.mega-menu-spotlight__sidebar-list > li[data-item-index]');
    const panels = root.querySelectorAll('.mega-menu-spotlight__panel[data-panel-index]');
    // The top-level nav <li> (e.g. "SEG Displays") that this spotlight
    // dropdown belongs to — used below to re-assert it as Shopify's active
    // menu item whenever the pointer is anywhere inside this dropdown.
    const topLevelItem = root.closest('.menu-list__list-item');
    const topLevelLink = topLevelItem?.querySelector('[ref="menuitem"]');

    const setActive = (index) => {
      items.forEach((li) => li.classList.toggle('is-active', li.dataset.itemIndex === index));
      panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.panelIndex === index));
    };

    // Shopify's header-menu.js tracks pointer movement (document.body
    // pointermove -> elementFromPoint) to keep the correct top-level item
    // "active" while the mouse travels diagonally from the nav bar into its
    // dropdown (a Safari hit-testing workaround). That reconciliation can
    // race with this dropdown's own open/close transition — since
    // spotlight's content is taller than the default mega menu and has
    // gaps between its sidebar/panel/CTA columns, it's more likely to lose
    // that race and re-activate a different, stale top-level item while
    // the mouse is still over here (most noticeably while crossing a gap).
    // Reclaiming on every pointermove inside the whole dropdown — not just
    // the sidebar rows — covers the panel and its own column gaps too.
    const reclaimActive = () => {
      if (!topLevelItem || topLevelLink?.getAttribute('aria-expanded') === 'true') return;
      topLevelItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    };

    root.addEventListener('pointermove', reclaimActive);

    items.forEach((li) => {
      li.addEventListener('pointerenter', () => {
        reclaimActive();
        setActive(li.dataset.itemIndex);
      });
      li.addEventListener('focusin', () => setActive(li.dataset.itemIndex));
    });
  });
}

/* ──────────────────────────────────────────────
   Calendly booking tracking (minimal, Calendly-only)
   Scoped-down version of the "Expert Consultation" listener from
   snippets/bds-tracking.liquid (not added to this theme yet — that
   snippet also covers WhatsApp/phone/email/form/chat touchpoints and
   ad-attribution capture, which are a separate, bigger rollout). This
   just listens for Calendly's own postMessage on booking completion
   and logs it to the same Google Sheet endpoint, so "Expert
   Consultation" leads land in the same place once that fuller rollout
   happens — without needing to touch this page again.

   Country detection mirrors the same domain/currency rules as that
   snippet, just done in JS via window.location/Shopify.currency since
   there's no Liquid `request.host` available from a plain script.
   ────────────────────────────────────────────── */
// function bdsCalendlyTrackingInit() {
//   const APPS_URL = 'https://script.google.com/macros/s/AKfycbyyEmzVdzB9HdO-HAEJBAWP9llMiSk-Mw29V0vmgfOOIJsK2TGh5Qqi3dQrsQUy6PCxpA/exec';

//   const detectCountry = () => {
//     const host = window.location.hostname.toLowerCase();
//     if (host.includes('.co.uk')) return 'UK';
//     if (host.includes('backdropsource.ca')) return 'CA';
//     if (host.includes('backdropsource.com')) return 'USA';
//     const currency = window.Shopify?.currency?.active;
//     if (currency === 'GBP') return 'UK';
//     if (currency === 'CAD') return 'CA';
//     if (currency === 'USD') return 'USA';
//     return 'XX';
//   };

//   window.addEventListener('message', (event) => {
//     if (event?.data?.event !== 'calendly.event_scheduled') return;

//     const payload = {
//       type: 'lead',
//       country: detectCountry(),
//       touchpoint: 'Expert Consultation',
//       timestamp: new Date().toISOString(),
//       page_url: window.location.href,
//     };

//     const qs = Object.entries(payload)
//       .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`)
//       .join('&');

//     try {
//       fetch(`${APPS_URL}?${qs}`, { method: 'GET', mode: 'no-cors', keepalive: true });
//     } catch (err) {
//       try {
//         navigator.sendBeacon(APPS_URL, JSON.stringify(payload));
//       } catch (err2) {
//         // Nothing more we can do — fail silently rather than break the page.
//       }
//     }
//   });
// }

/* ──────────────────────────────────────────────
   BD Hero Showcase
   ────────────────────────────────────────────── */
function bdHeroShowcaseInit() {
  document.querySelectorAll('[data-bhs-slider]').forEach((sliderEl) => {
    const sec = sliderEl.closest('[data-section-id]');
    if (!sec) return;

    const card = sec.querySelector('.bd-hs__card');
    if (!(card instanceof HTMLElement)) return;

    const slides = Array.from(sliderEl.querySelectorAll('[data-bhs-slide]')).filter(
      (el) => el instanceof HTMLElement
    );
    const dots = Array.from(sec.querySelectorAll('[data-bhs-dot]')).filter(
      (el) => el instanceof HTMLElement
    );
    const prevBtn = sec.querySelector('[data-bhs-prev]');
    const nextBtn = sec.querySelector('[data-bhs-next]');
    const total = slides.length;
    if (total === 0) return;

    const AUTOPLAY = card.dataset.bhAutoplay === 'true';
    const SPEED_MS = parseInt(card.dataset.bhSpeed || '5000', 10);

    let current = 0;
    /** @type {ReturnType<typeof setInterval> | null} */
    let autoTimer = null;

    /** @param {Element} dot */
    const fillOf = (dot) => dot.querySelector('[data-bhs-dot-fill]');

    /** @param {number} idx */
    const goTo = (idx) => {
      const next = ((idx % total) + total) % total;

      slides[current]?.classList.remove('is-active');
      dots[current]?.classList.remove('is-active');
      dots[current]?.setAttribute('aria-selected', 'false');

      slides[next]?.classList.add('is-active');
      dots[next]?.classList.add('is-active');
      dots[next]?.setAttribute('aria-selected', 'true');

      current = next;
    };

    const startProgress = () => {
      const fill = dots[current] && fillOf(dots[current]);
      if (!(fill instanceof HTMLElement)) return;
      if (!AUTOPLAY) {
        fill.style.transition = 'none';
        fill.style.width = '100%';
        return;
      }
      fill.style.transition = 'none';
      fill.style.width = '0%';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fill.style.transition = `width ${SPEED_MS}ms linear`;
          fill.style.width = '100%';
        });
      });
    };

    const resetProgress = () => {
      dots.forEach((dot) => {
        const fill = fillOf(dot);
        if (fill instanceof HTMLElement) {
          fill.style.transition = 'none';
          fill.style.width = '0%';
        }
      });
    };

    const startAuto = () => {
      if (!AUTOPLAY || total < 2) return;
      if (autoTimer) clearInterval(autoTimer);
      startProgress();
      autoTimer = setInterval(() => {
        goTo(current + 1);
        startProgress();
      }, SPEED_MS);
    };

    const stopAuto = () => {
      if (autoTimer) clearInterval(autoTimer);
      resetProgress();
    };

    if (prevBtn instanceof HTMLButtonElement) {
      prevBtn.addEventListener('click', () => { stopAuto(); goTo(current - 1); startAuto(); });
    }
    if (nextBtn instanceof HTMLButtonElement) {
      nextBtn.addEventListener('click', () => { stopAuto(); goTo(current + 1); startAuto(); });
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => { stopAuto(); goTo(index); startAuto(); });
    });

    sec.addEventListener('keydown', (e) => {
      if (!(e instanceof KeyboardEvent)) return;
      if (e.key === 'ArrowLeft') { stopAuto(); goTo(current - 1); startAuto(); }
      if (e.key === 'ArrowRight') { stopAuto(); goTo(current + 1); startAuto(); }
    });

    let touchStartX = 0;
    card.addEventListener('touchstart', (e) => {
      if (e instanceof TouchEvent) touchStartX = e.touches[0]?.clientX ?? 0;
    }, { passive: true });
    card.addEventListener('touchend', (e) => {
      if (!(e instanceof TouchEvent)) return;
      const dx = (e.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
      if (Math.abs(dx) > 50) { stopAuto(); goTo(dx < 0 ? current + 1 : current - 1); startAuto(); }
    }, { passive: true });

    card.addEventListener('mouseenter', () => { if (AUTOPLAY) stopAuto(); });
    card.addEventListener('mouseleave', () => { if (AUTOPLAY) startAuto(); });

    startAuto();
  });
}

/* ──────────────────────────────────────────────
   BD Hero — corner video preview modal
   ────────────────────────────────────────────── */
function bdHeroCornerVideoInit() {
  document.querySelectorAll('[data-bh-video-modal]').forEach((modalEl) => {
    if (!(modalEl instanceof HTMLElement)) return;
    const modal = modalEl;

    const sec = modal.closest('[data-section-id]');
    if (!sec) return;

    const body = modal.querySelector('[data-bh-video-body]');
    if (!(body instanceof HTMLElement)) return;

    const closeEls = modal.querySelectorAll('[data-bh-video-close]');
    const triggers = sec.querySelectorAll('[data-bh-video-trigger]');

    /** @type {HTMLElement | null} */
    let lastFocused = null;

    const closeModal = () => {
      modal.hidden = true;
      document.body.classList.remove('bh-video-modal-open');
      const video = body.querySelector('video');
      if (video instanceof HTMLVideoElement) video.pause();
      body.innerHTML = '';
      lastFocused?.focus();
    };

    /** @param {Element} trigger */
    const openModal = (trigger) => {
      const wrap = trigger.closest('[data-bh-video-wrap]');
      const tpl = wrap?.querySelector('[data-bh-video-template]');
      if (!(tpl instanceof HTMLTemplateElement)) return;

      lastFocused = trigger instanceof HTMLElement ? trigger : null;
      body.innerHTML = '';
      body.appendChild(tpl.content.cloneNode(true));
      modal.hidden = false;
      document.body.classList.add('bh-video-modal-open');

      const video = body.querySelector('video');
      if (video instanceof HTMLVideoElement) video.play().catch(() => {});

      const closeBtn = modal.querySelector('[data-bh-video-close]');
      if (closeBtn instanceof HTMLElement) closeBtn.focus();
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => openModal(trigger));
    });

    closeEls.forEach((el) => el.addEventListener('click', closeModal));

    modal.addEventListener('keydown', (e) => {
      if (e instanceof KeyboardEvent && e.key === 'Escape') closeModal();
    });
  });
}

/* ──────────────────────────────────────────────
   BD Testimonial Videos — track is sized (CSS) so 1.5 slides show at
   once; this only tracks which slide is most visible and keeps it
   ".is-active" (sharp), blurring the rest via CSS. Video playback itself
   is the shared [data-bh-video-wrap] modal handled by
   bdHeroCornerVideoInit() above.
   ────────────────────────────────────────────── */
function bdTestimonialVideoInit() {
  document.querySelectorAll('.bd-tv').forEach((section) => {
    const track = section.querySelector('[data-tv-track]');
    if (!(track instanceof HTMLElement)) return;

    const slides = Array.from(track.querySelectorAll('[data-tv-slide]'));
    if (slides.length === 0) return;

    const prevBtn = section.querySelector('[data-tv-prev]');
    const nextBtn = section.querySelector('[data-tv-next]');

    const setActive = (target) => {
      slides.forEach((slide) => slide.classList.toggle('is-active', slide === target));
    };

    if (slides.length > 1 && 'IntersectionObserver' in window) {
      const ratios = new Map();
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));

          let winner = slides[0];
          let best = -1;
          ratios.forEach((ratio, slide) => {
            if (ratio > best) {
              best = ratio;
              winner = slide;
            }
          });
          setActive(winner);
        },
        { root: track, threshold: [0, 0.25, 0.5, 0.75, 0.9, 1] }
      );
      slides.forEach((slide) => observer.observe(slide));
    }

    const getStep = () => {
      const first = slides[0];
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return first.getBoundingClientRect().width + gap;
    };

    const updateArrows = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = track.scrollLeft <= 4;
      if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = track.scrollLeft >= maxScroll - 4;
    };

    if (prevBtn instanceof HTMLButtonElement) {
      prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -getStep(), behavior: 'smooth' });
      });
    }
    if (nextBtn instanceof HTMLButtonElement) {
      nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: getStep(), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    updateArrows();
  });
}

/* ==========================================================
   BD Product Slider
   ========================================================== */

function bdProductSliderInit() {

  document.querySelectorAll('.bd-product-slider').forEach((section) => {

    const prevBtn = section.querySelector('[data-slider-prev]');
    const nextBtn = section.querySelector('[data-slider-next]');

    const tabs = section.querySelectorAll('.bd-feat-col__tab');
    const panels = section.querySelectorAll('.bd-feat-col__tab-content');

    function getActiveTrack() {
      return section.querySelector(
        '.bd-feat-col__tab-content:not(.hidden) [data-slider-track]'
      );
    }

    function getScrollAmount() {

      const track = getActiveTrack();

      if (!track) return 0;

      const firstCard = track.querySelector('.bd-product-slider__item');

      if (!firstCard) return 0;

      const gap = parseFloat(getComputedStyle(track).gap) || 24;

      return firstCard.offsetWidth + gap;

    }

    function updateButtons() {

      const track = getActiveTrack();

      if (!track) return;

      const maxScroll = track.scrollWidth - track.clientWidth;

      if (prevBtn) {
        prevBtn.disabled = track.scrollLeft <= 5;
      }

      if (nextBtn) {
        nextBtn.disabled = track.scrollLeft >= maxScroll - 5;
      }

    }

    function scrollSlider(direction) {

      const track = getActiveTrack();

      if (!track) return;

      track.scrollBy({
        left: getScrollAmount() * direction,
        behavior: 'smooth'
      });

    }

    prevBtn?.addEventListener('click', () => {
      scrollSlider(-1);
    });

    nextBtn?.addEventListener('click', () => {
      scrollSlider(1);
    });

    panels.forEach(panel => {

      const track = panel.querySelector('[data-slider-track]');

      if (!track) return;

      track.addEventListener(
        'scroll',
        updateButtons,
        { passive: true }
      );

    });

    window.addEventListener(
      'resize',
      updateButtons
    );

    /* -------------------------
       Tabs
    ------------------------- */

    tabs.forEach((tab, index) => {

      tab.addEventListener('click', () => {

        tabs.forEach(t => t.classList.remove('is-active'));
        panels.forEach(p => p.classList.add('hidden'));

        tab.classList.add('is-active');
        panels[index].classList.remove('hidden');

        const activeTrack = getActiveTrack();

        if (activeTrack) {
          activeTrack.scrollLeft = 0;
        }

        updateButtons();

      });

    });

    updateButtons();

  });

}
// ------
// 
function initBoothShowcase(section) {

  if (!section) return;

  const tabs = section.querySelectorAll('.booth-showcase__tab');
  const images = section.querySelectorAll('.booth-showcase__image');
  const contents = section.querySelectorAll('.booth-showcase__content-item');
  const viewMoreBtn = section.querySelector('#BoothViewMore');

  const json = section.querySelector('.booth-data');

  if (!json) return;

  const data = JSON.parse(json.textContent);

  function activateBooth(id) {

    tabs.forEach(tab => {
      tab.classList.toggle(
        'is-active',
        tab.dataset.booth === id
      );
      tab.setAttribute('aria-pressed', String(tab.dataset.booth === id));
    });

    images.forEach(image => {
      image.classList.toggle(
        'is-active',
        image.dataset.boothImage === id
      );
    });

    contents.forEach(content => {
      content.classList.toggle(
        'is-active',
        content.dataset.boothContent === id
      );
    });

    const booth = data.find(item => item.id === id);

    if (booth && viewMoreBtn) {
      viewMoreBtn.href = booth.url || '#';

      // "View 10 × 10 Booth Kits" comes from the section's View button text; the label sits beside an arrow icon
      const label = viewMoreBtn.querySelector('[data-booth-view-label]') || viewMoreBtn;
      const text = booth.viewLabel || booth.label;

      if (text) label.textContent = text;
    }
  }

  tabs.forEach(tab => {

    tab.addEventListener('click', () => {

      activateBooth(tab.dataset.booth);

    });

  });

}

/* ──────────────────────────────────────────────
   BD Use Cases — occasion slider
   Desktop: prev/next arrows scroll the track; hover reveals a card.
   Mobile: no hover, so whichever card is centred in the swipeable
   track gets an .is-active class instead (dots replace the arrows).
   ────────────────────────────────────────────── */
function bdUseCasesInit() {
  document.querySelectorAll('[data-boc-track]').forEach((trackEl) => {
    if (!(trackEl instanceof HTMLElement)) return;
    const track = trackEl;

    const sec = track.closest('[data-section-id]');
    if (!sec) return;

    const prevBtn = sec.querySelector('[data-boc-prev]');
    const nextBtn = sec.querySelector('[data-boc-next]');
    const cardWraps = Array.from(track.querySelectorAll('.bd-occasions__card-wrap')).filter(
      (el) => el instanceof HTMLElement
    );
    const dots = Array.from(sec.querySelectorAll('[data-boc-dot]')).filter(
      (el) => el instanceof HTMLElement
    );
    const firstWrap = cardWraps[0];
    if (!(firstWrap instanceof HTMLElement)) return;

    /** @param {number} direction */
    const scrollByCard = (direction) => {
      const trackStyle = getComputedStyle(track);
      const gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0;
      const amount = (firstWrap.getBoundingClientRect().width + gap) * direction;
      track.scrollBy({ left: amount, behavior: 'smooth' });
    };

    const updateArrows = () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 1;
      if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = track.scrollLeft <= 0;
      if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = track.scrollLeft >= maxScroll;
    };

    if (prevBtn instanceof HTMLButtonElement) {
      prevBtn.addEventListener('click', () => scrollByCard(-1));
    }
    if (nextBtn instanceof HTMLButtonElement) {
      nextBtn.addEventListener('click', () => scrollByCard(1));
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const target = cardWraps[index];
        if (target) track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      });
    });

    let ticking = false;
    const MOBILE_BREAKPOINT = 749; 
    const updateActiveCard = () => {
      ticking = false;
      if (window.innerWidth > MOBILE_BREAKPOINT) {                                                                       
        cardWraps.forEach((wrap) => {                                                                                    
          wrap.querySelector('.bd-occasions__card')?.classList.remove('is-active');                                      
        });                                                                                                              
        dots.forEach((dot) => {                                                                                          
          dot.classList.remove('is-active');                                                                             
          dot.setAttribute('aria-selected', 'false');                                                                    
        });                                                                                                              
      return;                                                                                                          
      }   
      const trackRect = track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;

      let closestIndex = 0;
      let closestDistance = Infinity;
      cardWraps.forEach((wrap, index) => {
        const wrapRect = wrap.getBoundingClientRect();
        const wrapCenter = wrapRect.left + wrapRect.width / 2;
        const distance = Math.abs(wrapCenter - trackCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      cardWraps.forEach((wrap, index) => {
        const card = wrap.querySelector('.bd-occasions__card');
        card?.classList.toggle('is-active', index === closestIndex);
      });

      dots.forEach((dot, index) => {
        const isActive = index === closestIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    const requestActiveUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateActiveCard);
    };

    track.addEventListener('scroll', updateArrows, { passive: true });
    track.addEventListener('scroll', requestActiveUpdate, { passive: true });
    window.addEventListener('resize', updateArrows);
    window.addEventListener('resize', requestActiveUpdate);
    updateArrows();
    updateActiveCard();
  });
}

/* ──────────────────────────────────────────────
   BD Order Showcase
   ────────────────────────────────────────────── */
function bdOrderShowcaseInit() {
  document.querySelectorAll('[data-bio-wrap]').forEach((wrapEl) => {
    if (!(wrapEl instanceof HTMLElement)) return;
    const wrap = wrapEl;

    const poster = wrap.querySelector('[data-bio-poster]');
    const trigger = wrap.querySelector('[data-bio-trigger]');
    const tpl = wrap.querySelector('[data-bio-template]');
    if (!(poster instanceof HTMLElement)) return;
    if (!(trigger instanceof HTMLElement)) return;
    if (!(tpl instanceof HTMLTemplateElement)) return;

    trigger.addEventListener('click', () => {
      wrap.appendChild(tpl.content.cloneNode(true));
      poster.remove();
      // Mobile poster is portrait (see .bd-order__media's 4:5 rule at
      // max-width:749px in custom.css) but the video itself should play
      // landscape — .is-playing switches the frame back to a 16:9 box on
      // mobile only; desktop's frame is already landscape (2:1) and is
      // untouched by this class.
      wrap.classList.add('is-playing');

      const video = wrap.querySelector('video');
      if (video instanceof HTMLVideoElement) video.play().catch(() => {});
    });
  });
}

/* ──────────────────────────────────────────────
   BD Category Bento — "See all categories" (mobile only)
   Reveals the cards hidden past mobile_visible_count and hides the
   button. The extra cards and the button are only ever visible below
   the 749px breakpoint (see custom.css); this just toggles state.
   ────────────────────────────────────────────── */
function bdCategoryBentoInit() {
  document.querySelectorAll('[data-cbb-toggle]').forEach((toggleEl) => {
    if (!(toggleEl instanceof HTMLButtonElement)) return;
    const toggle = toggleEl;

    const sec = toggle.closest('[data-section-id]');
    if (!sec) return;

    const grid = sec.querySelector('[data-cbb-grid]');
    if (!(grid instanceof HTMLElement)) return;

    const label = toggle.querySelector('[data-cbb-toggle-label]');

    const seeAllLabel = toggle.dataset.seeAllLabel || label?.textContent?.trim() || 'See all categories';
    const seeLessLabel = toggle.dataset.seeLessLabel || 'Show less';

    toggle.addEventListener('click', () => {
      const expanded = grid.classList.toggle('is-expanded');
      if (label) label.textContent = expanded ? seeLessLabel : seeAllLabel;

      if (!expanded) {
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });
}

/* ──────────────────────────────────────────────
   BD Our Journey — half-wheel timeline.
   Desktop: markers are laid out along an arc by JS (trig), and a
   single `current` index drives the active year/marker/panel plus the
   wheel's rotation. Click, arrow keys, mouse wheel and swipe all just
   call setActive(). On mobile the wheel is hidden and every milestone
   renders at once (see custom.css), so this mostly just keeps the
   .is-active class in sync for the dot/heading colour there.
   ────────────────────────────────────────────── */
function bdHeroShowcaseInit() {
  document.querySelectorAll('[data-bhs-slider]').forEach((sliderEl) => {
    const sec = sliderEl.closest('[data-section-id]');
    if (!sec) return;

    const card = sec.querySelector('.bd-hs__card');
    if (!(card instanceof HTMLElement)) return;

    const slides = Array.from(sliderEl.querySelectorAll('[data-bhs-slide]')).filter(
      (el) => el instanceof HTMLElement
    );
    const dots = Array.from(sec.querySelectorAll('[data-bhs-dot]')).filter(
      (el) => el instanceof HTMLElement
    );
    const prevBtn = sec.querySelector('[data-bhs-prev]');
    const nextBtn = sec.querySelector('[data-bhs-next]');
    const total = slides.length;
    if (total === 0) return;

    const AUTOPLAY = card.dataset.bhAutoplay === 'true';
    const SPEED_MS = parseInt(card.dataset.bhSpeed || '5000', 10);

    let current = 0;
    /** @type {ReturnType<typeof setInterval> | null} */
    let autoTimer = null;

    /** @param {Element} dot */
    const fillOf = (dot) => dot.querySelector('[data-bhs-dot-fill]');

    /** @param {number} idx */
    const goTo = (idx) => {
      const next = ((idx % total) + total) % total;

      slides[current]?.classList.remove('is-active');
      dots[current]?.classList.remove('is-active');
      dots[current]?.setAttribute('aria-selected', 'false');

      slides[next]?.classList.add('is-active');
      dots[next]?.classList.add('is-active');
      dots[next]?.setAttribute('aria-selected', 'true');

      current = next;
    };

    const startProgress = () => {
      const activeDot = dots[current];
      const fill = activeDot instanceof Element ? fillOf(activeDot) : null;
      if (!(fill instanceof HTMLElement)) return;
      if (!AUTOPLAY) {
        fill.style.transition = 'none';
        fill.style.width = '100%';
        return;
      }
      fill.style.transition = 'none';
      fill.style.width = '0%';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fill.style.transition = `width ${SPEED_MS}ms linear`;
          fill.style.width = '100%';
        });
      });
    };

    const resetProgress = () => {
      dots.forEach((dot) => {
        const fill = fillOf(dot);
        if (fill instanceof HTMLElement) {
          fill.style.transition = 'none';
          fill.style.width = '0%';
        }
      });
    };

    const startAuto = () => {
      if (!AUTOPLAY || total < 2) return;
      if (autoTimer) clearInterval(autoTimer);
      startProgress();
      autoTimer = setInterval(() => {
        goTo(current + 1);
        startProgress();
      }, SPEED_MS);
    };

    const stopAuto = () => {
      if (autoTimer) clearInterval(autoTimer);
      resetProgress();
    };

    if (prevBtn instanceof HTMLButtonElement) {
      prevBtn.addEventListener('click', () => { stopAuto(); goTo(current - 1); startAuto(); });
    }
    if (nextBtn instanceof HTMLButtonElement) {
      nextBtn.addEventListener('click', () => { stopAuto(); goTo(current + 1); startAuto(); });
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => { stopAuto(); goTo(index); startAuto(); });
    });

    sec.addEventListener('keydown', (e) => {
      if (!(e instanceof KeyboardEvent)) return;
      if (e.key === 'ArrowLeft') { stopAuto(); goTo(current - 1); startAuto(); }
      if (e.key === 'ArrowRight') { stopAuto(); goTo(current + 1); startAuto(); }
    });

    let touchStartX = 0;
    card.addEventListener('touchstart', (e) => {
      if (e instanceof TouchEvent) touchStartX = e.touches[0]?.clientX ?? 0;
    }, { passive: true });
    card.addEventListener('touchend', (e) => {
      if (!(e instanceof TouchEvent)) return;
      const dx = (e.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
      if (Math.abs(dx) > 50) { stopAuto(); goTo(dx < 0 ? current + 1 : current - 1); startAuto(); }
    }, { passive: true });

    card.addEventListener('mouseenter', () => { if (AUTOPLAY) stopAuto(); });
    card.addEventListener('mouseleave', () => { if (AUTOPLAY) startAuto(); });

    startAuto();
  });
}

/* ──────────────────────────────────────────────
   BD Category Bento — "See all categories" / "Show less" (mobile only)
   Toggles the cards hidden past mobile_visible_count and swaps the
   button's label to match. The extra cards and the button are only
   ever visible below the 749px breakpoint (see custom.css); this just
   toggles state.
   ────────────────────────────────────────────── */
function bdCategoryBentoInit() {
  document.querySelectorAll('[data-cbb-toggle]').forEach((toggleEl) => {
    if (!(toggleEl instanceof HTMLButtonElement)) return;
    const toggle = toggleEl;

    const sec = toggle.closest('[data-section-id]');
    if (!sec) return;

    const grid = sec.querySelector('[data-cbb-grid]');
    if (!(grid instanceof HTMLElement)) return;

    const label = toggle.querySelector('[data-cbb-toggle-label]');

    const seeAllLabel = toggle.dataset.seeAllLabel || label?.textContent?.trim() || 'See all categories';
    const seeLessLabel = toggle.dataset.seeLessLabel || 'Show less';

    toggle.addEventListener('click', () => {
      const expanded = grid.classList.toggle('is-expanded');
      if (label) label.textContent = expanded ? seeLessLabel : seeAllLabel;

      if (!expanded) {
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

}
/* ──────────────────────────────────────────────
   BD Values — no hover on touch, so highlight whichever card is
   nearest the viewport centre while scrolling, one at a time (same
   look as :hover, applied via .is-active — see the shared rules in
   custom.css). Scoped to the single-column mobile breakpoint (749px)
   since "nearest to centre" isn't a clean concept once cards sit
   side-by-side in the tablet 2-up grid.
   ────────────────────────────────────────────── */
function bdValuesInit() {
  document.querySelectorAll('[data-values-grid]').forEach((gridEl) => {
    if (!(gridEl instanceof HTMLElement)) return;
    const grid = gridEl;

    const cards = Array.from(grid.querySelectorAll('[data-values-card]')).filter(
      (el) => el instanceof HTMLElement
    );
    if (!cards.length) return;

    let ticking = false;

    const updateActive = () => {
      ticking = false;
      if (window.innerWidth > 749) {
        cards.forEach((card) => card.classList.remove('is-active'));
        return;
      }

      const viewportCenter = window.innerHeight / 2;
      let closestCard = cards[0];
      let closestDistance = Infinity;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const cardCenter = rect.top + rect.height / 2;
        const distance = Math.abs(cardCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestCard = card;
        }
      });

      cards.forEach((card) => card.classList.toggle('is-active', card === closestCard));
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateActive);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    requestUpdate();
  });
}

/* ──────────────────────────────────────────────
   BD Stats — count-up animation on scroll into view. Same logic as
   collection-header.liquid's inline script, but loops over every
   [data-bd-stats] row on the page (collection-header only ever has one
   instance per page, so it can get away with a single querySelector;
   this section could appear more than once).
   ────────────────────────────────────────────── */
function bdStatsInit() {
  const parseTarget = (raw) => {
    const match = raw.match(/^(\d+(?:\.\d+)?)(.*)/);
    return match ? { num: parseFloat(match[1]), suffix: match[2].trim() } : null;
  };

  const animateCounter = (el) => {
    const raw = el.dataset.countTarget;
    const parsed = parseTarget(raw);
    if (!parsed) return;

    const { num, suffix } = parsed;
    const duration = 1400;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * num);
      el.textContent = current + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  document.querySelectorAll('[data-bd-stats]').forEach((statsEl) => {
    const counters = statsEl.querySelectorAll('[data-count-target]');
    if (!counters.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        counters.forEach(animateCounter);
        observer.disconnect();
      });
    }, { threshold: 0.3 });

    observer.observe(statsEl);
  });
}


/* ──────────────────────────────────────────────
   BD Our Journey — half-wheel timeline.
   Desktop: markers are laid out along an arc by JS (trig), and a
   single `current` index drives the active year/marker/panel plus the
   wheel's rotation. Click, arrow keys, mouse wheel and swipe all just
   call setActive(). On mobile the wheel is hidden and every milestone
   renders at once (see custom.css), so this mostly just keeps the
   .is-active class in sync for the dot/heading colour there.
   ────────────────────────────────────────────── */
function journeyInit() {
    document.querySelectorAll('.journey').forEach((section) => {
        const wheelEl = section.querySelector('[data-wheel]');
        if (!(wheelEl instanceof HTMLElement)) return;
        const wheel = wheelEl;

        const markers = Array.from(section.querySelectorAll('.journey__marker')).filter(
            (el) => el instanceof HTMLElement
        );
        const years = Array.from(section.querySelectorAll('.journey__year-block'));
        const panels = Array.from(section.querySelectorAll('.journey__panel-item'));
        // Separate markup for the mobile timeline (see bd-journey.liquid) —
        // keep it in sync with the same `current` index so the active
        // dot/year/title/description highlight there too, even though
        // the wheel/markers driving that index are hidden on mobile.
        const mobileItems = Array.from(section.querySelectorAll('.journey__mobile-item'));
        if (!markers.length) return;
        const total = markers.length;
        /* ------------------------------------------
           Marker positions
        ------------------------------------------ */
        // Center of the full wheel (940x940)
        const cx = 470;
        const cy = 470;
        // Radius for markers
        const radius = 390;
        const startAngle = 110;
        const endAngle = 250;
        markers.forEach((marker, index) => {
            const angle = startAngle + ((endAngle - startAngle) / (Math.max(total - 1, 1))) * index;
            marker.dataset.angle = String(angle);
            const rad = angle * Math.PI / 180;
            const x = cx + Math.cos(rad) * radius;
            const y = cy + Math.sin(rad) * radius;
            marker.style.left = `${x}px`;
            marker.style.top = `${y}px`;
        });
        /* ------------------------------------------
           Active state
        ------------------------------------------ */
        let current = 0;

        /** @param {number} index */
        function setActive(index) {
            current = index;
            markers.forEach((el, i) => {
                el.classList.toggle('is-active', i === index);
            });
            years.forEach((el, i) => {
                el.classList.toggle('is-active', i === index);
            });
            panels.forEach((el, i) => {
                el.classList.toggle('is-active', i === index);
            });
            mobileItems.forEach((el, i) => {
                el.classList.toggle('is-active', i === index);
            });
            const activeMarker = markers[index];
            const angle = Number(activeMarker?.dataset.angle);
            wheel.style.transform = `rotate(${180 - angle}deg)`;
            const rotation = 180 - angle;
            // Push each label outward from the wheel's centre along its own
            // marker's angle, past the decorative rings (rings sit between
            // the marker radius and ~r+70), instead of a fixed offset that
            // only cleared the rings for markers on one side of the arc.
            const labelOutset = 70;
            markers.forEach((marker) => {
                const label = marker.querySelector('.journey__marker-label');
                if (label instanceof HTMLElement) {
                    const markerAngle = Number(marker.dataset.angle) || 0;
                    const markerRad = (markerAngle * Math.PI) / 180;
                    const dx = Math.cos(markerRad) * labelOutset;
                    const dy = Math.sin(markerRad) * labelOutset;
                    label.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${-rotation}deg)`;
                }
            });
        }
        setActive(0);
        /* ------------------------------------------
           Click
        ------------------------------------------ */
        markers.forEach((marker, index) => {
            marker.addEventListener('click', () => {
                setActive(index);
            });
        });
        /* ------------------------------------------
           Keyboard
        ------------------------------------------ */
        section.addEventListener('keydown', (e) => {
            if (!(e instanceof KeyboardEvent)) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                setActive((current + 1) % total);
            }
            if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                setActive((current - 1 + total) % total);
            }
        });
        /* ------------------------------------------
           Mouse wheel
        ------------------------------------------ */
        let wheelLock = false;
        let wheelDelta = 0;

        const WHEEL_THRESHOLD = 200;
        const WHEEL_COOLDOWN = 750;

        section.addEventListener(
            'wheel',
            (e) => {
                if (!(e instanceof WheelEvent)) return;

                e.preventDefault();

                // Accumulate the scroll amount instead of reacting
                // to every tiny mouse-wheel movement.
                wheelDelta += e.deltaY;

                // Ignore small movements
                if (Math.abs(wheelDelta) < WHEEL_THRESHOLD) return;

                // Prevent rapid switching between milestones
                if (wheelLock) return;

                wheelLock = true;

                if (wheelDelta > 0) {
                    setActive((current + 1) % total);
                } else {
                    setActive((current - 1 + total) % total);
                }

                // Reset accumulated scroll after changing slide
                wheelDelta = 0;

                setTimeout(() => {
                    wheelLock = false;
                }, WHEEL_COOLDOWN);
            },
            {
                passive: false
            }
        );
        /* ------------------------------------------
           Swipe
        ------------------------------------------ */
        let startX = 0;
        section.addEventListener('touchstart', (e) => {
            if (e instanceof TouchEvent) startX = e.touches[0]?.clientX ?? 0;
        }, {
            passive: true
        });
        section.addEventListener('touchend', (e) => {
            if (!(e instanceof TouchEvent)) return;
            const endX = e.changedTouches[0]?.clientX ?? startX;
            const diff = endX - startX;
            if (Math.abs(diff) < 50) return;
            if (diff < 0) {
                setActive((current + 1) % total);
            } else {
                setActive((current - 1 + total) % total);
            }
        }, {
            passive: true
        });
        /* ------------------------------------------
           Mobile scroll highlight
           The mobile timeline (.journey__mobile-item) has no
           wheel/markers to click — it's a plain stacked list, so
           "active" needs to follow ordinary page scroll instead:
           whichever item is nearest the viewport centre becomes
           active, one at a time, in both directions.
        ------------------------------------------ */
        if (mobileItems.length) {
            let mobileTicking = false;

            const updateActiveFromScroll = () => {
                mobileTicking = false;
                if (window.innerWidth > 989) return;

                const viewportCenter = window.innerHeight / 2;
                let closestIndex = 0;
                let closestDistance = Infinity;

                mobileItems.forEach((item, index) => {
                    const rect = item.getBoundingClientRect();
                    const itemCenter = rect.top + rect.height / 2;
                    const distance = Math.abs(itemCenter - viewportCenter);
                    if (distance < closestDistance) {
                        closestDistance = distance;
                        closestIndex = index;
                    }
                });

                if (closestIndex !== current) setActive(closestIndex);
            };

            const requestScrollUpdate = () => {
                if (mobileTicking) return;
                mobileTicking = true;
                requestAnimationFrame(updateActiveFromScroll);
            };

            window.addEventListener('scroll', requestScrollUpdate, { passive: true });
            window.addEventListener('resize', requestScrollUpdate);
            requestScrollUpdate();
        }
    });
}
/* ------------------------------------------
   Init
------------------------------------------ */
document.addEventListener('DOMContentLoaded', journeyInit);
document.addEventListener('shopify:section:load', journeyInit);
document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    if (!(block instanceof Element)) return;
    const section = block.closest('.journey');
    if (!section) return;
    const blocks = Array.from(section.querySelectorAll('.journey__year-block'));
    const index = blocks.indexOf(block);
    if (index === -1) return;
    const marker = section.querySelectorAll('.journey__marker')[index];
    if (marker instanceof HTMLElement) marker.click();
});


/* ──────────────────────────────────────────────
   BD Collection Gallery
   Main tabs swap the whole panel (card + images).
   Inside a panel, sub-collection pills swap only the
   image group — the card's heading/description/CTA
   belong to the parent collection and never change.
   Each panel has one pagination nav, rebuilt against
   whichever sub-collection's image group is active.
   ────────────────────────────────────────────── */
function bdCollectionGalleryInit() {
  const PAGE_SIZE = 11;

  // Liquid can't check whether a given "Prefix_N.ext" filename was
  // actually uploaded — file_url/file_img_url both happily return a
  // URL for a file that doesn't exist, it just 404s — so every
  // scanned number renders as a candidate tile. Wait for each
  // candidate's real load/error result and drop the 404s before any
  // tab/pagination logic runs, so counts (and pages) only reflect
  // images that actually exist.
  function pruneBrokenTiles(section) {
    const tiles = Array.from(section.querySelectorAll('[data-gallery-tile]'));
    const checks = tiles.map((tile) => {
      const img = tile.querySelector('img');
      if (!(img instanceof HTMLImageElement)) return Promise.resolve();
      if (img.complete) {
        if (img.naturalWidth === 0) tile.remove();
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => {
          tile.remove();
          resolve();
        }, { once: true });
      });
    });
    return Promise.all(checks);
  }

  document.querySelectorAll('.bd-gallery').forEach((section) => {
    pruneBrokenTiles(section).then(() => initGallerySection(section, PAGE_SIZE));
  });
}

function initGallerySection(section, PAGE_SIZE) {
    const tabs = Array.from(section.querySelectorAll('[data-gallery-tabs] [data-gallery-tab]'));
    const panels = Array.from(section.querySelectorAll('[data-gallery-panel]'));

    function setActiveTab(index) {
      tabs.forEach((tab, i) => {
        tab.classList.toggle('is-active', i === index);
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      panels.forEach((panel, i) => panel.classList.toggle('is-active', i === index));
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => setActiveTab(i));
    });

    panels.forEach((panel) => {
      const subTabs = Array.from(panel.querySelectorAll('[data-sub-tabs] > [data-sub-tab]'));
      const imageGroups = Array.from(panel.querySelectorAll('[data-sub-panel]'));
      const paginationEl = panel.querySelector('[data-gallery-pagination]');

      function renderPagination(groupIndex, page) {
        const group = imageGroups[groupIndex];
        if (!group || !(paginationEl instanceof HTMLElement)) return;

        const tiles = Array.from(group.querySelectorAll('.bd-gallery__tile'));
        const totalPages = Math.max(Math.ceil(tiles.length / PAGE_SIZE), 1);
        const current = Math.min(Math.max(page, 1), totalPages);

        tiles.forEach((tile, i) => {
          const tilePage = Math.floor(i / PAGE_SIZE) + 1;
          tile.toggleAttribute('data-gallery-page-hidden', tilePage !== current);
        });

        paginationEl.innerHTML = '';
        paginationEl.classList.toggle('is-visible', totalPages > 1);
        if (totalPages <= 1) return;

        const makeBtn = (label, targetPage, isActive, isDisabled) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'bd-gallery__page-btn' + (isActive ? ' is-active' : '');
          btn.textContent = label;
          btn.disabled = !!isDisabled;
          btn.addEventListener('click', () => renderPagination(groupIndex, targetPage));
          return btn;
        };

        paginationEl.appendChild(makeBtn('‹', current - 1, false, current === 1));
        for (let p = 1; p <= totalPages; p += 1) {
          paginationEl.appendChild(makeBtn(String(p), p, p === current, false));
        }
        paginationEl.appendChild(makeBtn('›', current + 1, false, current === totalPages));
      }

      function setActiveSub(index) {
        subTabs.forEach((tab, i) => tab.classList.toggle('is-active', i === index));
        imageGroups.forEach((group, i) => group.classList.toggle('is-active', i === index));
        renderPagination(index, 1);
      }

      subTabs.forEach((tab, i) => {
        tab.addEventListener('click', () => setActiveSub(i));
      });

      if (imageGroups.length) renderPagination(0, 1);
    });

    if (tabs.length) setActiveTab(0);
}
/* ──────────────────────────────────────────────
   BD Our Journey — Timeline edition
   Desktop: a horizontal rail of markers plus a single detail panel
   below it. Selection is click/keyboard only (marker, prev/next, or
   arrow keys) — everything sits in one row, so there's no vertical
   run left to scroll-spy against. The rail-fill tracks the active
   marker's horizontal position instead of a vertical one.
   Mobile is untouched: it keeps its own scroll-spy (closest item to
   the viewport centre), exactly as before.
   ────────────────────────────────────────────── */
function bdJourneyTimelineInit() {
  document.querySelectorAll('.bd-journey-tl').forEach((section) => {
    const items = Array.from(section.querySelectorAll('[data-jt-item]'));
    const triggers = Array.from(section.querySelectorAll('[data-jt-trigger]'));
    const panels = Array.from(section.querySelectorAll('[data-jt-panel]'));
    const mobileItems = Array.from(section.querySelectorAll('.bd-journey-tl__mobile-item'));
    const railFill = section.querySelector('[data-jt-rail-fill]');
    const list = section.querySelector('[data-jt-list]');
    const prevBtn = section.querySelector('[data-jt-prev]');
    const nextBtn = section.querySelector('[data-jt-next]');
    if (!items.length) return;
    const total = items.length;

    let current = 0;

    function updateRail(index) {
      if (!(railFill instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
      const marker = triggers[index];
      if (!(marker instanceof HTMLElement)) return;
      const target = marker.offsetLeft + marker.offsetWidth / 2;
      const listWidth = list.scrollWidth;
      const pct = listWidth ? (target / listWidth) * 100 : 0;
      railFill.style.width = pct + '%';
    }

    function setActive(index) {
      // Wrapping around (e.g. last -> first via next) would otherwise
      // read as "prev" by plain index comparison — treat a wrap as
      // continuing in the same direction the user was already moving.
      const forwardWrap = current === total - 1 && index === 0;
      const backwardWrap = current === 0 && index === total - 1;
      const direction = forwardWrap ? 'next' : backwardWrap ? 'prev' : index >= current ? 'next' : 'prev';

      current = index;
      items.forEach((el, i) => el.classList.toggle('is-active', i === index));
      panels.forEach((el, i) => el.classList.toggle('is-active', i === index));

      const activePanel = panels[index];
      if (activePanel instanceof HTMLElement) {
        activePanel.classList.remove('bd-jt-from-left', 'bd-jt-from-right');
        // Force a reflow so re-adding the same class restarts the animation.
        void activePanel.offsetWidth;
        activePanel.classList.add(direction === 'next' ? 'bd-jt-from-right' : 'bd-jt-from-left');
      }

      updateRail(index);
    }

    triggers.forEach((trigger, i) => {
      trigger.addEventListener('click', () => setActive(i));
    });

    if (prevBtn instanceof HTMLElement) {
      prevBtn.addEventListener('click', () => setActive((current - 1 + total) % total));
    }
    if (nextBtn instanceof HTMLElement) {
      nextBtn.addEventListener('click', () => setActive((current + 1) % total));
    }

    section.addEventListener('keydown', (e) => {
      if (!(e instanceof KeyboardEvent)) return;
      if (window.innerWidth <= 989) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActive((current + 1) % total);
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActive((current - 1 + total) % total);
      }
    });

    setActive(0);
    window.addEventListener('resize', () => updateRail(current));

    /* ------------------------------------------
       Scroll-in reveal — the desktop block fades
       and lifts into place the first time it
       enters the viewport. Progressive enhancement:
       the hidden starting state is only ever
       applied once JS confirms it can reveal it.
    ------------------------------------------ */
    const desktopEl = section.querySelector('.bd-journey-tl__desktop');
    if (desktopEl instanceof HTMLElement && 'IntersectionObserver' in window) {
      desktopEl.classList.add('bd-jt-will-reveal');
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );
      revealObserver.observe(desktopEl);
    }

    /* ------------------------------------------
       Mobile scroll-spy (unchanged) — whichever
       mobile item is nearest the viewport centre
       becomes active, following ordinary scroll.
    ------------------------------------------ */
    if (mobileItems.length) {
      let mobileCurrent = 0;
      let ticking = false;

      const updateActiveFromScroll = () => {
        ticking = false;
        if (window.innerWidth > 989) return;

        const viewportCenter = window.innerHeight / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;

        mobileItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const itemCenter = rect.top + rect.height / 2;
          const distance = Math.abs(itemCenter - viewportCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== mobileCurrent) {
          mobileCurrent = closestIndex;
          mobileItems.forEach((el, i) => el.classList.toggle('is-active', i === closestIndex));
        }
      };

      const requestScrollUpdate = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(updateActiveFromScroll);
      };

      window.addEventListener('scroll', requestScrollUpdate, { passive: true });
      window.addEventListener('resize', requestScrollUpdate);
      requestScrollUpdate();
    }
  });
}
document.addEventListener('DOMContentLoaded', bdJourneyTimelineInit);
document.addEventListener('shopify:section:load', bdJourneyTimelineInit);
document.addEventListener('shopify:block:select', (event) => {
  const block = event.target;
  if (!(block instanceof Element)) return;
  const section = block.closest('.bd-journey-tl');
  if (!section) return;
  const items = Array.from(section.querySelectorAll('[data-jt-item]'));
  const index = items.indexOf(block);
  if (index === -1) return;
  const trigger = section.querySelectorAll('[data-jt-trigger]')[index];
  if (trigger instanceof HTMLElement) trigger.click();
});

/*------Product Artificial template JS-----STARTS---------*/
/* ──────────────────────────────────────────────
   Product Specs Tabs (product-specs-tabs.liquid)
   Delegated from the section root rather than bound per-button, so
   it keeps working after pdartInit()'s syncOtherSections() morphs in
   a different product's tabs (possibly a different count of them) —
   the root itself is what the Section Rendering API preserves, only
   its children get swapped.
   ────────────────────────────────────────────── */
function productSpecsTabsInit() {
  document.querySelectorAll('.product-specs-tabs').forEach((root) => {
    root.addEventListener('click', (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-spec-tab]') : null;
      if (!btn) return;
      root.querySelectorAll('[data-spec-tab]').forEach((tab) => tab.classList.remove('is-active'));
      root.querySelectorAll('[data-spec-panel]').forEach((panel) => panel.classList.remove('is-active'));
      btn.classList.add('is-active');
      root.querySelector(`[data-spec-panel="${btn.dataset.specTab}"]`)?.classList.add('is-active');
    });
  });
}

/* ──────────────────────────────────────────────
   Product FAQ (product-faq-metafields.liquid)
   Same delegation reasoning as Product Specs Tabs above — bound to
   the section root so it survives being morphed to a different
   product's FAQ list.
   ────────────────────────────────────────────── */
function productFaqInit() {
  document.querySelectorAll('.product-faq-metafields').forEach((root) => {
    const DURATION = 300;
    const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

    const closeItem = (item) => {
      const body = item.querySelector('.faq-body');
      if (!body) return;
      const anim = body.animate([{ height: body.scrollHeight + 'px' }, { height: '0px' }], {
        duration: DURATION,
        easing: EASING,
      });
      anim.onfinish = () => {
        item.removeAttribute('open');
        body.style.height = '';
        anim.cancel();
      };
    };

    const openItem = (item) => {
      root.querySelectorAll('details.product-faq-metafields__item').forEach((other) => {
        if (other !== item && other.open) closeItem(other);
      });
      item.setAttribute('open', '');
      const body = item.querySelector('.faq-body');
      if (!body) return;
      const h = body.scrollHeight;
      const anim = body.animate([{ height: '0px' }, { height: h + 'px' }], { duration: DURATION, easing: EASING });
      anim.onfinish = () => {
        body.style.height = 'auto';
        anim.cancel();
      };
    };

    root.addEventListener('click', (event) => {
      const summary = event.target instanceof Element ? event.target.closest('summary') : null;
      if (!summary) return;
      const item = summary.closest('details.product-faq-metafields__item');
      if (!item) return;
      event.preventDefault();
      item.open ? closeItem(item) : openItem(item);
    });
  });
}

/* ──────────────────────────────────────────────
   BD Product — Artificial (product.artificial-floral.liquid)
   "Choose Size" and "Choose Design" are each a real, visually-hidden
   <select> paired with a synced visual (size pills / design swatch
   grid) — the selects fire the actual change events and drive all
   the logic; clicking a pill/swatch only updates its paired select
   and dispatches change on it. Design changes fetch a sibling
   product's public /products/{handle}.js and swap the gallery, size
   options, SKU, style label, price and add-to-cart variant id in
   place, no navigation.
   ────────────────────────────────────────────── */
function pdartInit() {
  document.querySelectorAll('.pdart').forEach((section) => {
    // ── Refs ──────────────────────────────────────────────
    const thumbsWrap = section.querySelector('[data-pdart-thumbs]');
    const mainWrap = section.querySelector('[data-pdart-main]');
    const titleEl = section.querySelector('[data-pdart-title]');
    const skuEl = section.querySelector('[data-pdart-sku]');
    const totalEl = section.querySelector('[data-pdart-total]');
    const styleLabelEl = section.querySelector('[data-pdart-style-label]');
    const unavailableMsg = section.querySelector('[data-pdart-unavailable]');
    const sizeGroup = section.querySelector('[data-pdart-size-group]');
    const sizeSelect = section.querySelector('[data-pdart-size-select]');
    const sizePillsWrap = section.querySelector('[data-pdart-size-pills]');
    const designSelect = section.querySelector('[data-pdart-design-select]');
    const swatchGrid = section.querySelector('[data-pdart-swatch-grid]');
    const variantIdInput = section.querySelector('[ref="variantId"]');
    const variantJsonEl = section.querySelector('[data-pdart-variants]');
    const addToCartBtn = section.querySelector('[ref="addToCartButton"]');
    const addToCartComponent = addToCartBtn?.closest('add-to-cart-component');
    const addToCartTextEl = addToCartBtn?.querySelector('.add-to-cart-text__content span span');

    // Other sections on the page that show content for *this* product —
    // synced to whichever design is selected. "How to Order" is static
    // per-design content doesn't apply there, so it's left alone.
    const recommendationsEl = document.querySelector('product-recommendations');
    const specsSection = document.querySelector('.product-specs-tabs')?.closest('[id^="shopify-section-"]');
    const blogSection = document.querySelector('.product-image-text')?.closest('[id^="shopify-section-"]');
    const faqSection = document.querySelector('.product-faq-metafields')?.closest('[id^="shopify-section-"]');

    let currentVariants = [];
    try {
      currentVariants = JSON.parse(variantJsonEl?.textContent || '[]');
    } catch (e) {
      currentVariants = [];
    }

    // ── Helpers ───────────────────────────────────────────
    const formatMoney = (cents) => '$' + (cents / 100).toFixed(2);

    const setAddToCartState = (available) => {
      if (addToCartComponent && typeof addToCartComponent.disable === 'function') {
        available ? addToCartComponent.enable() : addToCartComponent.disable();
      } else if (addToCartBtn instanceof HTMLButtonElement) {
        addToCartBtn.disabled = !available;
      }
      if (addToCartTextEl) addToCartTextEl.textContent = available ? 'Add to cart' : 'Sold out';
    };

    // ── Thumbnail / main-image slider (same product) ─────
    function bindGalleryClicks() {
      const thumbs = Array.from(section.querySelectorAll('[data-pdart-thumb]'));
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const index = thumb.dataset.pdartThumb;
          thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
          section.querySelectorAll('[data-pdart-slide]').forEach((slide) => {
            slide.classList.toggle('is-active', slide.dataset.pdartSlide === index);
          });
        });
      });
    }
    bindGalleryClicks();

    // ── Size <select> → real variant switch (current product) ─
    // Same "select drives the logic, pills are just a synced visual"
    // split as the design swatches below.
    function onSizeChange() {
      if (!sizeSelect) return;
      const value = sizeSelect.value;
      const match = currentVariants.find((v) => v.option1 === value);

      if (sizePillsWrap) {
        Array.from(sizePillsWrap.querySelectorAll('[data-pdart-size-pill]')).forEach((pill) => {
          pill.classList.toggle('is-active', pill.dataset.pdartSizePill === value);
        });
      }

      if (match) {
        if (variantIdInput) variantIdInput.value = match.id;
        if (totalEl) totalEl.textContent = formatMoney(match.price);
        if (skuEl) skuEl.textContent = 'SKU: ' + (match.sku || '');
        setAddToCartState(match.available);
        if (unavailableMsg) unavailableMsg.hidden = true;
      } else {
        if (variantIdInput) variantIdInput.value = '';
        setAddToCartState(false);
        if (unavailableMsg) unavailableMsg.hidden = false;
      }
    }
    sizeSelect?.addEventListener('change', onSizeChange);

    function bindSizePillClicks() {
      if (!sizePillsWrap || !sizeSelect) return;
      Array.from(sizePillsWrap.querySelectorAll('[data-pdart-size-pill]')).forEach((pill) => {
        pill.addEventListener('click', () => {
          if (pill.classList.contains('is-active')) return;
          sizeSelect.value = pill.dataset.pdartSizePill;
          sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
    bindSizePillClicks();

    // ── Rebuild gallery from a fetched product's images ───
    // The main slide must rebuild even when there's no thumbs
    // wrapper to update (e.g. the page loaded on a single-image
    // product) — the two used to be gated behind one shared guard,
    // which silently skipped the main image too.
    function rebuildGallery(images) {
      if (!mainWrap) return;
      mainWrap.innerHTML = '';
      images.forEach((src, index) => {
        const slide = document.createElement('div');
        slide.className = 'pdart__slide' + (index === 0 ? ' is-active' : '');
        slide.dataset.pdartSlide = String(index);
        slide.innerHTML = `<img src="${src}" loading="lazy" alt="">`;
        mainWrap.appendChild(slide);
      });

      if (thumbsWrap) {
        thumbsWrap.innerHTML = '';
        thumbsWrap.hidden = images.length <= 1;
        images.forEach((src, index) => {
          const thumbBtn = document.createElement('button');
          thumbBtn.type = 'button';
          thumbBtn.className = 'pdart__thumb' + (index === 0 ? ' is-active' : '');
          thumbBtn.dataset.pdartThumb = String(index);
          thumbBtn.innerHTML = `<img src="${src}" loading="lazy" alt="">`;
          thumbsWrap.appendChild(thumbBtn);
        });
      }

      bindGalleryClicks();
    }

    // ── Rebuild the size <select> from a fetched product's variants ─
    function rebuildSizeSelect(fetchedProduct, preferredValue) {
      if (!sizeGroup || !sizeSelect) return;
      const values = [];
      fetchedProduct.variants.forEach((v) => {
        if (v.option1 && !values.includes(v.option1)) values.push(v.option1);
      });

      if (!values.length) {
        sizeGroup.hidden = true;
        return;
      }
      sizeGroup.hidden = false;

      const preferredMatch = values.includes(preferredValue) ? preferredValue : null;
      const defaultVariant =
        fetchedProduct.variants.find((v) => v.option1 === preferredMatch) ||
        fetchedProduct.variants.find((v) => v.available) ||
        fetchedProduct.variants[0];

      sizeSelect.innerHTML = '';
      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        if (value === defaultVariant.option1) option.selected = true;
        sizeSelect.appendChild(option);
      });

      if (sizePillsWrap) {
        sizePillsWrap.innerHTML = '';
        values.forEach((value) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'pdart__size-pill' + (value === defaultVariant.option1 ? ' is-active' : '');
          pill.dataset.pdartSizePill = value;
          pill.textContent = value;
          sizePillsWrap.appendChild(pill);
        });
        bindSizePillClicks();
      }

      currentVariants = fetchedProduct.variants;

      if (variantIdInput) variantIdInput.value = defaultVariant.id;
      if (totalEl) totalEl.textContent = formatMoney(defaultVariant.price);
      if (skuEl) skuEl.textContent = 'SKU: ' + (defaultVariant.sku || '');
      setAddToCartState(defaultVariant.available);
      if (unavailableMsg) unavailableMsg.hidden = true;
    }

    // ── Sync Related Products / Specs Tabs / Blog / FAQ to the newly
    //    selected design ─────────────────────────────────────────
    // Specs Tabs / FAQ interactivity itself (tab switching, accordion
    // open/close) doesn't need re-binding here — productSpecsTabsInit()
    // and productFaqInit() above already delegate from each section's
    // stable root rather than binding per-button/per-<details>, so
    // they keep working automatically no matter how many tabs/items
    // the newly rendered content below has.
    async function syncOtherSections(fetchedProduct, productUrl) {
      // Related Products already fetches + morphs itself whenever its
      // data-product-id changes (see product-recommendations.js) — we
      // just need to point it at the new product and clear the
      // "already performed" flag so it doesn't skip the refetch.
      if (recommendationsEl instanceof HTMLElement) {
        recommendationsEl.dataset.productId = String(fetchedProduct.id);
        recommendationsEl.dataset.recommendationsPerformed = 'false';
      }

      const sectionsToRender = [specsSection, blogSection, faqSection].filter(Boolean);
      if (!sectionsToRender.length) return;

      try {
        const { sectionRenderer, normalizeSectionId } = await import('@theme/section-renderer');
        const targetUrl = new URL(productUrl, window.location.origin);
        await Promise.all(
          sectionsToRender.map((sectionEl) =>
            sectionRenderer.renderSection(normalizeSectionId(sectionEl.id), { url: targetUrl })
          )
        );
      } catch (error) {
        console.error('[Product Artificial] Failed to sync page sections:', error);
      }
    }

    // ── Design change (fires on the hidden <select>, not on the
    //    swatch buttons) → fetch sibling product, swap in place ──
    if (swatchGrid && designSelect) {
      const swatches = Array.from(swatchGrid.querySelectorAll('[data-pdart-swatch]'));

      async function loadDesign(selectedOption) {
        const productUrl = selectedOption?.dataset.productUrl;
        if (!productUrl) return;

        const preferredSizeValue = sizeSelect?.value;
        swatches.forEach((s) => s.classList.add('is-loading'));

        try {
          const response = await fetch(productUrl + '.js');
          if (!response.ok) throw new Error('Product fetch failed');
          const fetchedProduct = await response.json();

          const selectedId = selectedOption.value;
          swatches.forEach((s) => s.classList.toggle('is-active', s.dataset.productId === selectedId));
          if (titleEl && fetchedProduct.title) titleEl.textContent = fetchedProduct.title;
          if (styleLabelEl) styleLabelEl.textContent = selectedOption.textContent.trim();

          const images = (fetchedProduct.images || []).map((src) =>
            src.startsWith('//') || src.startsWith('http') ? src : '/' + src
          );
          if (images.length) rebuildGallery(images);

          rebuildSizeSelect(fetchedProduct, preferredSizeValue);

          // Not awaited: the rest of the page catches up in the
          // background rather than delaying this section's own swap.
          syncOtherSections(fetchedProduct, productUrl);
        } catch (error) {
          console.error('[Product Artificial] Failed to load design swatch:', error);
        } finally {
          swatches.forEach((s) => s.classList.remove('is-loading'));
        }
      }

      designSelect.addEventListener('change', () => {
        loadDesign(designSelect.options[designSelect.selectedIndex]);
      });

      // Swatches are just a visual, mouse-friendly overlay on top of
      // the select — clicking one only updates + fires the select's
      // change event; loadDesign() above is the single place the
      // actual fetch/swap logic runs from.
      swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          if (swatch.classList.contains('is-active')) return;
          designSelect.value = swatch.dataset.productId;
          designSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  });
}

/* ──────────────────────────────────────────────
   BD Product — Fabric (product.fabric.liquid)
   Gallery slider + lightbox, the artwork-upload popup, and the Step 2
   add-on price total are all ported from product-design-main.liquid /
   product-main-v2.liquid, just under this section's own .pfab prefix
   and written straight into custom.js from the start (no inline
   <script>, matching how product-specs-tabs/product-faq were moved
   here). "Need Design Help?" needs no JS of its own — it's a plain
   [data-mockup-quote] anchor, handled by the theme-wide
   mockup-request-popup section's own global listener.
   ────────────────────────────────────────────── */
function pfabInit() {
  document.querySelectorAll('.pfab').forEach((section) => {
    // ── Gallery slider ───────────────────────────────────
    function showSlide(index) {
      section.querySelectorAll('[data-pfab-slide]').forEach((slide) => {
        slide.classList.toggle('is-active', slide.dataset.pfabSlide === String(index));
      });
      section.querySelectorAll('[data-pfab-thumb-trigger]').forEach((trigger) => {
        trigger.classList.toggle('is-active', trigger.dataset.pfabThumbTrigger === String(index));
      });
    }
    section.querySelectorAll('[data-pfab-thumb-trigger]').forEach((button) => {
      button.addEventListener('click', () => showSlide(button.dataset.pfabThumbTrigger));
    });

    // ── Step 2 add-on total + Select Fabric variant matching ──
    const totalEl = section.querySelector('[data-pfab-total]');
    const addonSelects = Array.from(section.querySelectorAll('.pfab__addon-select'));
    const formatMoney = (cents) => '$' + (cents / 100).toFixed(2);
    let basePriceCents = parseInt(totalEl?.dataset.baseCents, 10) || 0;

    const updateTotal = () => {
      if (!totalEl) return;
      const addonCents = addonSelects.reduce((sum, select) => {
        const price = parseFloat(select.options[select.selectedIndex]?.dataset.price || '0');
        return sum + Math.round(price * 100);
      }, 0);
      totalEl.textContent = formatMoney(basePriceCents + addonCents);
    };

    addonSelects.forEach((select) => select.addEventListener('change', updateTotal));
    if (totalEl) updateTotal();

    const variantSelects = Array.from(section.querySelectorAll('[data-pfab-variant-option]'));
    const variantsJsonEl = section.querySelector('[data-pfab-variants]');
    const variantIdInput = section.querySelector('[ref="variantId"]');

    if (variantSelects.length && variantsJsonEl && variantIdInput) {
      let variants = [];
      try {
        variants = JSON.parse(variantsJsonEl.textContent);
      } catch (e) {
        variants = [];
      }

      const priceEl = section.querySelector('[data-pfab-price]');
      const compareEl = section.querySelector('[data-pfab-compare]');
      const badgeEl = section.querySelector('[data-pfab-badge]');
      const skuEl = section.querySelector('[data-pfab-sku]');
      const unavailableEl = section.querySelector('[data-pfab-unavailable]');
      const addToCartContainer = section.querySelector('[ref="addToCartButtonContainer"]');
      const addToCartButton = addToCartContainer?.querySelector('[ref="addToCartButton"]');
      const addToCartTextEl = addToCartButton?.querySelector('.add-to-cart-text__content span span');

      const setAddToCartState = (available) => {
        if (addToCartContainer && typeof addToCartContainer.enable === 'function' && typeof addToCartContainer.disable === 'function') {
          available ? addToCartContainer.enable() : addToCartContainer.disable();
        } else if (addToCartButton) {
          addToCartButton.disabled = !available;
        }
      };

      const updateVariant = () => {
        const selected = variantSelects.map((select) => ({
          index: parseInt(select.dataset.pfabVariantOption, 10),
          value: select.value,
        }));
        const match = variants.find((variant) =>
          selected.every(({ index, value }) => variant.options[index] === value)
        );

        if (match) {
          variantIdInput.value = match.id;
          basePriceCents = match.price;

          if (priceEl) priceEl.textContent = formatMoney(match.price);

          if (match.compare_at_price > match.price) {
            if (compareEl) { compareEl.textContent = formatMoney(match.compare_at_price); compareEl.hidden = false; }
            if (badgeEl) {
              const discount = Math.round(((match.compare_at_price - match.price) / match.compare_at_price) * 100);
              badgeEl.textContent = discount + '% OFF';
              badgeEl.hidden = false;
            }
          } else {
            if (compareEl) compareEl.hidden = true;
            if (badgeEl) badgeEl.hidden = true;
          }

          if (skuEl) {
            skuEl.textContent = match.sku ? 'SKU: ' + match.sku : '';
            skuEl.hidden = !match.sku;
          }

          if (unavailableEl) unavailableEl.hidden = true;
          setAddToCartState(match.available);
        } else {
          variantIdInput.value = '';
          if (unavailableEl) unavailableEl.hidden = false;
          setAddToCartState(false);
        }

        updateTotal();
      };

      variantSelects.forEach((select) => select.addEventListener('change', updateVariant));
    }

    // ── Lightbox ──────────────────────────────────────────
    const lb = document.getElementById('pfab-lb-' + section.dataset.sectionId);
    if (lb) {
      const lbSlides = lb.querySelectorAll('[data-pfab-lb-slide]');
      const lbThumbs = lb.querySelectorAll('[data-pfab-lb-thumb]');
      const lbCount = lbSlides.length;
      let lbCurrent = 0;

      const lbShow = (i) => {
        lbCurrent = ((i % lbCount) + lbCount) % lbCount;
        lbSlides.forEach((s, j) => s.classList.toggle('is-active', j === lbCurrent));
        lbThumbs.forEach((t, j) => t.classList.toggle('is-active', j === lbCurrent));
        lbThumbs[lbCurrent]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      };

      const lbOpen = (index) => {
        lbShow(index);
        lb.classList.add('is-open');
        lb.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        lb.querySelector('.pfab-lightbox__close')?.focus();
      };

      const lbClose = () => {
        lb.classList.remove('is-open');
        lb.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      };

      section.querySelector('[data-pfab-image-frame]')?.addEventListener('click', () => {
        const active = section.querySelector('[data-pfab-slide].is-active');
        lbOpen(active ? parseInt(active.dataset.pfabSlide, 10) : 0);
      });

      lb.querySelector('.pfab-lightbox__backdrop')?.addEventListener('click', lbClose);
      lb.querySelector('.pfab-lightbox__close')?.addEventListener('click', lbClose);
      lb.querySelector('.pfab-lightbox__nav--prev')?.addEventListener('click', () => lbShow(lbCurrent - 1));
      lb.querySelector('.pfab-lightbox__nav--next')?.addEventListener('click', () => lbShow(lbCurrent + 1));
      lbThumbs.forEach((t) => t.addEventListener('click', () => lbShow(parseInt(t.dataset.pfabLbThumb, 10))));

      document.addEventListener('keydown', (e) => {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') lbClose();
        if (e.key === 'ArrowLeft') lbShow(lbCurrent - 1);
        if (e.key === 'ArrowRight') lbShow(lbCurrent + 1);
      });
    }

    // ── Artwork upload popup ─────────────────────────────
    const popup = document.getElementById('pfab-artwork-' + section.dataset.sectionId);
    if (!popup) return;

    const propInput = document.getElementById('pfab-artwork-prop-' + section.dataset.sectionId);
    const summaryEl = section.querySelector('[data-pfab-artwork-summary]');
    const uploadCard = section.querySelector('[data-pfab-artwork-trigger]');
    let activeTab = 'direct';

    const openPopup = () => {
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      popup.querySelector('.pfab-artwork-popup__close')?.focus();
    };
    const closePopup = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    uploadCard?.addEventListener('click', openPopup);
    uploadCard?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openPopup();
      }
    });
    popup.querySelector('.pfab-artwork-popup__backdrop')?.addEventListener('click', closePopup);
    popup.querySelector('.pfab-artwork-popup__close')?.addEventListener('click', closePopup);

    popup.querySelectorAll('[data-pfab-aw-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        activeTab = tab.dataset.pfabAwTab;
        popup.querySelectorAll('[data-pfab-aw-tab]').forEach((t) => t.classList.toggle('is-active', t === tab));
        popup.querySelectorAll('[data-pfab-aw-panel]').forEach((p) => {
          p.dataset.pfabAwPanel === activeTab ? p.removeAttribute('hidden') : p.setAttribute('hidden', '');
        });
      });
    });

    const dropzone = popup.querySelector('[data-pfab-aw-dropzone]');
    const fileInput = popup.querySelector('[data-pfab-aw-file]');
    const filenameEl = popup.querySelector('[data-pfab-aw-filename]');

    /* Artwork goes to Uploadcare so properties[Artwork] carries a CDN URL
       the team can open. Saving file.name put a filename on the order with
       no file behind it. See snippets/bds-uploadcare.liquid. */
    const ucChannel = 'pfab-' + section.dataset.sectionId;
    let artwork = null;

    const showArtwork = (picked) => {
      if (!picked) return;
      artwork = picked;
      if (filenameEl) filenameEl.textContent = '✔ ' + picked.label;
      dropzone?.classList.add('has-file');
      dropzone?.classList.remove('is-invalid');
    };

    const uploadFailed = (err) => {
      console.error('[PFAB] Uploadcare unavailable - falling back to the browser file picker', err);
      fileInput?.click();
    };

    const openPicker = () => {
      if (!window.BDSUploadcare) return uploadFailed('BDSUploadcare missing');
      window.BDSUploadcare.pick(ucChannel)
        .then((picked) => {
          if (picked) showArtwork(picked);
        })
        .catch(uploadFailed);
    };

    popup.querySelector('[data-pfab-aw-click]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      openPicker();
    });
    dropzone?.addEventListener('click', openPicker);

    // Only reachable through the fallback above.
    fileInput?.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      artwork = { url: '', name: file.name, label: file.name };
      if (filenameEl) filenameEl.textContent = '✔ ' + file.name;
      dropzone?.classList.add('has-file');
      dropzone?.classList.remove('is-invalid');
    });

    dropzone?.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.classList.add('is-drag-over');
    });
    dropzone?.addEventListener('dragleave', () => dropzone.classList.remove('is-drag-over'));
    dropzone?.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('is-drag-over');
      const file = e.dataTransfer.files?.[0];
      if (!file) return;

      if (!window.BDSUploadcare) return uploadFailed('BDSUploadcare missing');
      if (filenameEl) filenameEl.textContent = 'Uploading…';
      window.BDSUploadcare.upload(ucChannel, file).then(showArtwork).catch(uploadFailed);
    });

    popup.querySelector('[data-pfab-aw-copy]')?.addEventListener('click', (e) => {
      const email = e.currentTarget.dataset.pfabAwCopy;
      navigator.clipboard?.writeText(email).catch(() => {});
      const btn = e.currentTarget;
      btn.classList.add('is-copied');
      setTimeout(() => btn.classList.remove('is-copied'), 1800);
    });

    popup.querySelector('[data-pfab-aw-save]')?.addEventListener('click', () => {
      const urlInput = popup.querySelector('[data-pfab-aw-url]');
      const checkInput = popup.querySelector('[data-pfab-aw-check]');
      let value = '';
      let summary = '';

      if (activeTab === 'direct') {
        if (!artwork) {
          dropzone?.classList.add('is-invalid');
          return;
        }
        // The URL is what the order needs; the bare filename is only the
        // fallback for when Uploadcare could not run at all.
        value = artwork.url || artwork.name;
        summary = '✔ File: ' + artwork.label;
      } else if (activeTab === 'link') {
        const url = urlInput?.value.trim();
        if (!url) {
          urlInput?.classList.add('is-invalid');
          urlInput?.focus();
          return;
        }
        urlInput?.classList.remove('is-invalid');
        value = url;
        summary = '✔ Link saved';
      } else {
        if (!checkInput?.checked) {
          checkInput?.closest('.pfab-artwork-popup__check-label')?.classList.add('is-invalid');
          return;
        }
        checkInput?.closest('.pfab-artwork-popup__check-label')?.classList.remove('is-invalid');
        value = 'Will email artwork after checkout';
        summary = '✔ Will email artwork after checkout';
      }

      if (propInput) propInput.value = value;
      if (summaryEl) summaryEl.textContent = summary;
      uploadCard?.classList.add('has-artwork');
      closePopup();
    });

    document.addEventListener('keydown', (e) => {
      if (popup.classList.contains('is-open') && e.key === 'Escape') closePopup();
    });
  });
}

/* ──────────────────────────────────────────────
   BD Ministry Collections — carousel arrows
   Section: sections/bd-ministry-collections.liquid

   The track scrolls natively (scroll-snap + overflow-x), so swipe,
   trackpad and keyboard already work with zero JS. This only wires the
   arrow buttons, keeps their disabled state in sync, and hides the whole
   nav when there is nothing to scroll.

   Idempotent: listeners are attached once per track, guarded by a flag on
   the element, so theme-editor reloads never double-bind.
   ────────────────────────────────────────────── */
function bdMinistryCollectionsInit(root) {
  const scope = root || document;

  scope.querySelectorAll('[data-bmc-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-bmc-track]');
    const nav = carousel.querySelector('[data-bmc-nav]');
    const prev = carousel.querySelector('[data-bmc-prev]');
    const next = carousel.querySelector('[data-bmc-next]');

    if (!track) return;

    const step = () => {
      const card = track.querySelector('.bd-mc__item');
      if (!card) return track.clientWidth;

      const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return card.offsetWidth + gap;
    };

    const sync = () => {
      // 2px of slack absorbs sub-pixel scroll positions.
      const max = track.scrollWidth - track.clientWidth;

      if (nav) nav.hidden = max <= 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };

    if (!track._bmcBound) {
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', sync, { passive: true });

      // Card widths depend on the container, so re-check on resize.
      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(track);
      } else {
        window.addEventListener('resize', sync);
      }

      track._bmcBound = true;
    }

    sync();
  });
}

document.addEventListener('DOMContentLoaded', () => bdMinistryCollectionsInit());
document.addEventListener('shopify:section:load', (event) => {
  bdMinistryCollectionsInit(event.target);
});

/* ──────────────────────────────────────────────
   BD Ministry Categories — category tabs + product carousel
   Section: sections/bd-ministry-categories.liquid

   Page-specific on purpose: bdProductSliderInit drives the homepage's
   bd-featured-products-slider and is hard-wired to .bd-feat-col__tab-content,
   so this keeps its own hooks rather than reaching into that.

   Arrows act on whichever panel is active, so one nav serves every category.
   Idempotent: binding is guarded by a flag on the section element.
   ────────────────────────────────────────────── */
function bdMinistryCategoriesInit(root) {
  const scope = root || document;

  scope.querySelectorAll('[data-bmcat]').forEach((section) => {
    const tabs = Array.from(section.querySelectorAll('[data-bmcat-tab]'));
    const panels = Array.from(section.querySelectorAll('[data-bmcat-panel]'));
    const nav = section.querySelector('[data-bmcat-nav]');
    const prev = section.querySelector('[data-bmcat-prev]');
    const next = section.querySelector('[data-bmcat-next]');

    if (!tabs.length || !panels.length) return;

    const activeTrack = () => {
      const panel = panels.find((p) => !p.hidden);
      return panel ? panel.querySelector('[data-bmcat-track]') : null;
    };

    const step = (track) => {
      const card = track.querySelector('.bd-mcat__item');
      if (!card) return track.clientWidth;

      const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
      return card.offsetWidth + gap;
    };

    const sync = () => {
      const track = activeTrack();

      // No track means the active category has no products — hide the arrows.
      if (!track) {
        if (nav) nav.hidden = true;
        return;
      }

      const max = track.scrollWidth - track.clientWidth;

      if (nav) nav.hidden = max <= 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };

    const select = (index) => {
      tabs.forEach((tab, i) => {
        const on = i === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        // Roving tabindex: only the selected tab is in the tab order.
        if (on) {
          tab.removeAttribute('tabindex');
        } else {
          tab.setAttribute('tabindex', '-1');
        }
      });

      panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        panel.classList.toggle('is-active', i === index);
      });

      const track = activeTrack();
      if (track) track.scrollLeft = 0;

      sync();
    };

    if (!section._bmcatBound) {
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(i));

        tab.addEventListener('keydown', (event) => {
          let target = null;
          if (event.key === 'ArrowRight') target = (i + 1) % tabs.length;
          if (event.key === 'ArrowLeft') target = (i - 1 + tabs.length) % tabs.length;
          if (target === null) return;

          event.preventDefault();
          select(target);
          tabs[target].focus();
        });
      });

      prev?.addEventListener('click', () => {
        const track = activeTrack();
        if (track) track.scrollBy({ left: -step(track), behavior: 'smooth' });
      });

      next?.addEventListener('click', () => {
        const track = activeTrack();
        if (track) track.scrollBy({ left: step(track), behavior: 'smooth' });
      });

      // One listener per track — scroll events only fire on the visible one.
      section.querySelectorAll('[data-bmcat-track]').forEach((track) => {
        track.addEventListener('scroll', sync, { passive: true });
      });

      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(section);
      } else {
        window.addEventListener('resize', sync);
      }

      section._bmcatBound = true;
    }

    sync();
  });
}

document.addEventListener('DOMContentLoaded', () => bdMinistryCategoriesInit());
document.addEventListener('shopify:section:load', (event) => {
  bdMinistryCategoriesInit(event.target);
});

// Selecting a category block in the theme editor should reveal its panel.
document.addEventListener('shopify:block:select', (event) => {
  const tab = event.target.matches?.('[data-bmcat-tab]')
    ? event.target
    : event.target.querySelector?.('[data-bmcat-tab]');

  if (tab) tab.click();
});

/* ──────────────────────────────────────────────
   BD Ministry Get Started — expandable option columns + share-idea form
   Section: sections/bd-ministry-getstarted.liquid

   Columns expand independently — opening one does not close the others,
   since these are parallel entry points rather than steps in a wizard.

   The form posts FormData to /contact#ContactForm with the same field names
   as mockup-request-popup.liquid, so submissions land in the existing
   Shopify contact enquiries alongside mockup requests.

   Idempotent: binding is guarded by a flag on the section element.
   ────────────────────────────────────────────── */
function bdMinistryGetStartedInit(root) {
  const scope = root || document;

  scope.querySelectorAll('[data-bmget]').forEach((section) => {
    if (section._bmgetBound) return;

    // Entry option: reveals every other column. aria-controls carries a
    // space-separated id list, so split it rather than treating it as one id.
    section.querySelectorAll('[data-bmget-reveal]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const ids = (trigger.getAttribute('aria-controls') || '').split(/\s+/).filter(Boolean);
        const cols = ids.map((id) => document.getElementById(id)).filter(Boolean);
        if (!cols.length) return;

        // Drive every column from one state so they cannot drift apart.
        const open = cols[0].hidden;
        cols.forEach((col) => { col.hidden = !open; });

        section
          .querySelectorAll('[data-bmget-reveal]')
          .forEach((el) => el.setAttribute('aria-expanded', open ? 'true' : 'false'));

        // Collapsing the flow resets it, so reopening starts clean.
        if (!open) {
          cols.forEach((col) => {
            col.querySelectorAll('[data-bmget-panel]').forEach((p) => { p.hidden = true; });
            col.querySelectorAll('[data-bmget-form]').forEach((f) => { f.hidden = true; });
            col.querySelectorAll('[data-bmget-toggle], [data-bmget-form-open]')
              .forEach((el) => el.setAttribute('aria-expanded', 'false'));
          });
        }
      });
    });

    // A column's option button and its + button both control the same panel.
    section.querySelectorAll('[data-bmget-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;

        const open = panel.hidden;
        panel.hidden = !open;

        /* "I have an idea or template to share" is a column that collapsed to
           its form, so it has no opener button left to press. Closing the
           whole section hides every [data-bmget-form] — including this one —
           and the pass that originally revealed it runs once at init and
           never again. So the form opened the first time, and after a close
           the panel reopened empty with nothing able to bring it back.

           Re-asserting on open fixes it regardless of what hid it. */
        if (open) {
          panel
            .querySelectorAll('[data-bmget-form][data-bmget-direct-form]')
            .forEach((wrap) => {
              wrap.hidden = false;
            });
        }

        // Keep every control pointing at this panel in sync.
        section
          .querySelectorAll('[aria-controls="' + toggle.getAttribute('aria-controls') + '"]')
          .forEach((el) => el.setAttribute('aria-expanded', open ? 'true' : 'false'));
      });
    });

    section.querySelectorAll('[data-bmget-scroll]').forEach((button) => {
      button.addEventListener('click', () => {
        const target = document.querySelector(button.dataset.bmgetScroll);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    section.querySelectorAll('[data-bmget-form-open]').forEach((button) => {
      button.addEventListener('click', () => {
        // Walk forward to this trigger's own form, skipping any note in between,
        // so a column with several children opens the right one.
        let wrap = button.nextElementSibling;
        while (wrap && !wrap.matches('[data-bmget-form]')) {
          if (wrap.matches('[data-bmget-form-open]')) { wrap = null; break; }
          wrap = wrap.nextElementSibling;
        }
        if (!wrap) return;

        wrap.hidden = !wrap.hidden;
        button.setAttribute('aria-expanded', wrap.hidden ? 'false' : 'true');
        if (!wrap.hidden) wrap.querySelector('input, select, textarea')?.focus();
      });
    });

    /* A column whose only child is a form has no reason to make the visitor
       click twice. "I have an idea or template to share" opened a panel
       containing one button — "Click here to upload image or share your
       idea" — which then opened the form. The button restated the option
       above it and bought nothing.

       Detected rather than configured: any column that resolves to exactly
       one form and no sibling choices collapses to the form itself. Add a
       second child later and the opener returns on its own, because the
       condition stops matching. */
    section.querySelectorAll('[data-bmget-panel]').forEach((panel) => {
      const openers = panel.querySelectorAll('[data-bmget-form-open]');
      const siblings = panel.querySelectorAll(
        '.bd-mget__child:not([data-bmget-form-open])'
      );
      if (openers.length !== 1 || siblings.length) return;

      const opener = openers[0];
      let wrap = opener.nextElementSibling;
      while (wrap && !wrap.matches('[data-bmget-form]')) wrap = wrap.nextElementSibling;
      if (!wrap) return;

      opener.hidden = true;
      opener.dataset.bmgetDirectForm = "true";
      // Marked on the wrap as well as the opener: the panel toggle needs to
      // recognise it at click time, and by then the opener is display:none
      // and easy to miss.
      wrap.dataset.bmgetDirectForm = "true";
      // The panel is still closed until the option is clicked; this only
      // decides what is inside it when it opens.
      wrap.hidden = false;
    });

    section.querySelectorAll('[data-bmget-form-el]').forEach((form) => {
      bdMinistryGetStartedForm(form);
    });

    section._bmgetBound = true;
  });
}

function bdMinistryGetStartedForm(form) {
  const wrap = form.closest('[data-bmget-form]');
  const errorEl = form.querySelector('[data-bmget-error]');
  const successEl = wrap ? wrap.querySelector('[data-bmget-success]') : null;
  const submitEl = form.querySelector('[data-bmget-submit]');
  const emailEl = form.querySelector('input[name="email"]');

  const showError = (message) => {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  };

  const clearError = () => {
    if (errorEl) errorEl.hidden = true;
    emailEl?.removeAttribute('aria-invalid');
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearError();

    const email = emailEl?.value.trim() || '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      emailEl?.setAttribute('aria-invalid', 'true');
      showError('Please enter a valid email address.');
      emailEl?.focus();
      return;
    }

    /* Re-entrancy guard. The button used to be disabled only after the
       Uploadcare upload and the counter and geo lookups had all resolved —
       several seconds during which the click had visibly done nothing, the
       button still said "Send my idea", and nothing stopped a second submit.
       People clicked again, and that second click ran the whole handler a
       second time: C-Lead-US#002 and #003 in the sheet are one person's idea
       submitted twice, three seconds apart.

       The flag is on the form rather than on submitEl.disabled alone because
       Enter in a text field submits too, without ever touching the button. */
    if (form.dataset.bmgetBusy === '1') return;
    form.dataset.bmgetBusy = '1';

    const original = submitEl ? submitEl.textContent : '';
    if (submitEl) {
      submitEl.disabled = true;
      submitEl.textContent = 'Sending…';
    }

    const value = (name) => form.querySelector(`[name="${name}"]`)?.value.trim() || '';
    const file = form.querySelector('input[type="file"]')?.files?.[0];

    /* ── Church & Ministry idea leads ─────────────────────────────────────
       This used to POST to /contact and stop there, which meant the enquiry
       existed only as an email in whichever inbox Shopify's contact form
       points at: no row anywhere, no request id, no auto-reply to the
       sender, and no ad attribution. It now follows the same path as the
       two 2D forms — counter, geo, sheet, EmailJS — with its own
       C-Lead-US# series on its own tab.

       The /contact POST is kept as a backstop: it is the one delivery that
       does not depend on a third-party script loading. */
    const LEAD_COUNTER_URL = 'https://script.google.com/macros/s/AKfycbysdhq2w-U1_HhSkCQceMuwEDMGjL5lJUMtZWQ7LQIYaXe0E50vhOTebHLp6jr4v1Ofuw/exec';
    const LEAD_SHEET_URL   = 'https://script.google.com/macros/s/AKfycbzghc4A_71FF86tempYXTXNR3AmkqW3fOiB3BlDa9C9ETodkoV8TG135wX-P5vChVso/exec';
    const LEAD_GEO_URL     = 'https://api.ipgeolocation.io/ipgeo?apiKey=d8219c97f06142f1b715280e15e42214';

    const LEAD_TARGET   = 'Church-Lead';
    const LEAD_FORMTYPE = 'CHURCH_LEAD';
    const LEAD_PREFIX   = 'C-Lead-IN#';

    const LEAD_EMAILJS_PUBLIC_KEY = 'Qy8GnlfeI_jayLu47';
    const LEAD_EMAILJS_SERVICE    = 'service_t2nlztf';
    /* Its own EmailJS template because this form carries Banner Type and a
       free-text idea, which the 2D templates have no rows for. Until the id
       is pasted in, the EmailJS step is skipped and the sheet write plus the
       /contact fallback still run — so the form is never broken by the
       placeholder, it is only quieter. */
    const LEAD_EMAILJS_STORE_TEMPLATE    = 'template_0p9junc';
    const LEAD_EMAILJS_CUSTOMER_TEMPLATE = 'template_djtx8sa';
    const LEAD_STORE_EMAIL = 'churches@backdropsource.com';
    const LEAD_SUBJECT     = 'Backdropsource - Church & Ministry Idea';

    const leadSet = (id) => id && id.indexOf('PASTE_') === -1;

    const withTimeout = (promise, ms) =>
      Promise.race([promise, new Promise((r) => setTimeout(() => r(null), ms))]);

    // Nice-to-have, never worth failing an enquiry over — both resolve null.
    const leadCounter = async () => {
      try {
        const qs = new URLSearchParams({ country: 'IN', formType: LEAD_FORMTYPE });
        const res = await fetch(LEAD_COUNTER_URL + '?' + qs.toString(), { redirect: 'follow' });
        if (!res.ok) return null;
        return (await res.json()).counter;
      } catch (err) { return null; }
    };

    const leadGeo = async () => {
      try {
        const res = await fetch(LEAD_GEO_URL);
        if (!res.ok) return null;
        const g = await res.json();
        return {
          city: g.city || '',
          state: g.state_prov || '',
          tz: (g.time_zone && g.time_zone.name) || '',
          now: (g.time_zone && g.time_zone.current_time) || '',
        };
      } catch (err) { return null; }
    };

    /* Uploadcare rather than contact[attachment]: Shopify's contact form has
       a size ceiling that silently rejects the whole POST, which is what the
       "large files can fail to send" hint under the field was apologising
       for. A CDN link survives any size and is what the studio actually
       wants in the sheet. Falls back to the plain attachment if the widget
       is unavailable. */
    let uploadedUrl = '';
    if (file && window.BDSUploadcare) {
      const up = await withTimeout(
        window.BDSUploadcare.upload('bmget-' + (form.dataset.bmgetChannel || 'idea'), file).catch(() => null),
        20000
      );
      if (up && up.url) uploadedUrl = up.url;
    }

    const fileLink = uploadedUrl || value('link') || '';

    const body = [
      `Church Name: ${value('church') || '—'}`,
      `Banner Type: ${value('banner_type') || '—'}`,
      '',
      'Idea / description:',
      value('description') || '—',
      '',
      uploadedUrl ? `Uploaded file: ${uploadedUrl}` : `Attached file: ${file ? file.name : 'none'}`,
      `File link: ${value('link') || 'none'}`,
    ].join('\n');

    const [counter, geo] = await Promise.all([
      withTimeout(leadCounter(), 12000),
      withTimeout(leadGeo(), 9000),
    ]);
    const token = LEAD_PREFIX + String(counter || '0').padStart(3, '0');
    if (window.BDSLeadAttribution && window.BDSLeadAttribution.setRequestId) window.BDSLeadAttribution.setRequestId(token);

    const clickField = (cls) => form.querySelector('.' + cls)?.value || '';

    const sheetParams = new URLSearchParams({
      target: LEAD_TARGET,
      origin: window.location.origin,
      token: token,
      requestId: token,
      country: 'IN',
      customerName: value('church') || 'Church & Ministry enquiry',
      emailId: email,
      bannerType: value('banner_type'),
      description: value('description'),
      fileLink: fileLink,
      customerCity: geo ? geo.city : '',
      customerState: geo ? geo.state : '',
      customerTimezone: geo ? geo.tz : '',
      customerTime: geo ? geo.now : '',
      clickId: clickField('js_click_id'),
      clickSource: clickField('js_click_source') || 'Direct',
      submissionDate: clickField('js_submission_date'),
    });

    const sendSheet = () => {
      const payload = sheetParams.toString();
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/x-www-form-urlencoded' });
        if (navigator.sendBeacon(LEAD_SHEET_URL, blob)) return Promise.resolve('queued');
      }
      return fetch(LEAD_SHEET_URL, {
        method: 'POST', mode: 'no-cors', keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload,
      });
    };

    const sendLeadEmails = async () => {
      if (typeof emailjs === 'undefined') return;
      if (!leadSet(LEAD_EMAILJS_STORE_TEMPLATE)) return;
      emailjs.init(LEAD_EMAILJS_PUBLIC_KEY);

      /* Signature fields are passed rather than hard-coded in the template so
         the same template can be pointed at another storefront later without
         being rebuilt — the other Backdropsource templates already work this
         way. phone_href is separate from phone_number because the display
         form (+1 (650) 614-1888) is not a valid tel: target. */
      const vars = {
        shop_token: token,
        to_email: LEAD_STORE_EMAIL,
        shop_name: 'Backdropsource - IN',
        subject: LEAD_SUBJECT,
        from_name: value('church') || 'there',
        church_name: value('church') || '—',
        banner_type: value('banner_type') || '—',
        description: value('description') || '—',
        file_link: fileLink || '',
        customer_email: email,
        customer_city: geo ? geo.city : '',
        customer_state: geo ? geo.state : '',
        executive_name: 'Church & Ministry Team',
        designation: 'Backdropsource',
        phone_number: '+1 (650) 614-1888',
        phone_href: 'tel:+16506141888',
        whatsapp_number: '17372041523',
        store_email: LEAD_STORE_EMAIL,
        website: 'https://www.backdropsource.com',
      };

      await emailjs.send(LEAD_EMAILJS_SERVICE, LEAD_EMAILJS_STORE_TEMPLATE, vars);
      if (leadSet(LEAD_EMAILJS_CUSTOMER_TEMPLATE)) {
        await emailjs.send(LEAD_EMAILJS_SERVICE, LEAD_EMAILJS_CUSTOMER_TEMPLATE,
          Object.assign({}, vars, { to_email: email }));
      }
    };

    const data = new FormData();
    data.append('form_type', 'contact');
    data.append('utf8', '✓');
    data.append('contact[name]', value('church') || 'Church & Ministry enquiry');
    data.append('contact[email]', email);
    data.append('contact[body]', 'Request ID: ' + token + '\n' + body);
    // Only when Uploadcare did not take it — otherwise the link is in the body.
    if (file && !uploadedUrl) data.append('contact[attachment]', file, file.name);

    try {
      /* allSettled, not all: a failed EmailJS send or a blocked sheet write
         must not tell someone their idea did not go through when the contact
         POST carried it anyway. */
      const results = await Promise.allSettled([
        sendSheet(),
        sendLeadEmails(),
        fetch('/contact#ContactForm', {
          method: 'POST',
          body: data,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        }).then((r) => { if (!r.ok) throw new Error('contact ' + r.status); return r; }),
      ]);

      const failed = results
        .map((r, i) => (r.status === 'rejected' ? ['sheet', 'emailjs', 'contact'][i] : null))
        .filter(Boolean);
      if (failed.length) console.warn('[BMGET] delivery failed: ' + failed.join(', '));
      if (failed.length === results.length) throw new Error('all deliveries failed');

      if (window.BDSUploadcare) window.BDSUploadcare.clear('bmget-' + (form.dataset.bmgetChannel || 'idea'));
      form.hidden = true;
      if (successEl) successEl.hidden = false;

      // Only when the counter actually answered — "Your reference is
      // C-Lead-US#000" is worse than showing no reference at all.
      const refEl = wrap ? wrap.querySelector('[data-bmget-ref]') : null;
      if (refEl && counter) {
        refEl.textContent = 'Your reference is ' + token;
        refEl.hidden = false;
      }
    } catch (error) {
      showError('Something went wrong — please try again, or email us directly.');
    } finally {
      form.dataset.bmgetBusy = '';
      if (submitEl) {
        submitEl.disabled = false;
        submitEl.textContent = original;
      }
    }
  });
}

document.addEventListener('DOMContentLoaded', () => bdMinistryGetStartedInit());
document.addEventListener('shopify:section:load', (event) => {
  bdMinistryGetStartedInit(event.target);
});

/* ──────────────────────────────────────────────
   BD Ministry Our Work — centred showcase carousel with side peeks
   Section: sections/bd-ministry-work.liquid

   The track scrolls natively with scroll-snap, so swipe and keyboard work
   without this. Added here: arrow wrap-around, active-slide and dot state,
   and optional autoplay.

   Index comes from scrollLeft / stride, where stride is one slide plus the
   gap. The track's side padding means scrollLeft 0 already has slide one
   centred, so no offset correction is needed.

   Wrapping last -> first uses an instant jump; a smooth one would visibly
   race through every slide in between.

   Idempotent: binding is guarded by a flag on the section element.
   ────────────────────────────────────────────── */
function bdMinistryWorkInit(root) {
  const scope = root || document;

  scope.querySelectorAll('[data-bmwk]').forEach((section) => {
    const track = section.querySelector('[data-bmwk-track]');
    if (!track) return;

    const slides = Array.from(section.querySelectorAll('[data-bmwk-slide]'));
    const dots = Array.from(section.querySelectorAll('[data-bmwk-dot]'));
    const prev = section.querySelector('[data-bmwk-prev]');
    const next = section.querySelector('[data-bmwk-next]');

    if (!slides.length) return;

    // Measured, not assumed: slide width is a CSS var that changes per
    // breakpoint, and the gap comes from the stylesheet too.
    const stride = () => {
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return slides[0].offsetWidth + gap;
    };

    const current = () => {
      const step = stride();
      if (!step) return 0;
      return Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / step)));
    };

    const goTo = (index, instant) => {
      const clamped = (index + slides.length) % slides.length;
      track.scrollTo({
        left: clamped * stride(),
        behavior: instant ? 'auto' : 'smooth',
      });
    };

    const sync = () => {
      const active = current();

      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === active);
      });

      dots.forEach((dot, i) => {
        const on = i === active;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };

    if (!section._bmwkBound) {
      prev?.addEventListener('click', () => {
        const i = current();
        goTo(i - 1, i === 0);
      });

      next?.addEventListener('click', () => {
        const i = current();
        goTo(i + 1, i === slides.length - 1);
      });

      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => goTo(i, false));
      });

      track.addEventListener('scroll', sync, { passive: true });

      // Slide width is viewport-dependent, so re-measure and re-centre.
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => {
          goTo(current(), true);
          sync();
        }).observe(track);
      } else {
        window.addEventListener('resize', sync);
      }

      if (section.dataset.bmwkAutoplay === 'true' && slides.length > 1) {
        bdMinistryWorkAutoplay(section, track, slides, goTo, current);
      }

      section._bmwkBound = true;
    }

    sync();
  });
}

function bdMinistryWorkAutoplay(section, track, slides, goTo, current) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const interval = parseInt(section.dataset.bmwkInterval, 10) || 5000;
  let timer = null;
  let paused = false;

  const tick = () => {
    // Skip while backgrounded, hovered or focused rather than racing ahead.
    if (paused || document.hidden) return;

    const i = current();
    goTo(i + 1, i === slides.length - 1);
  };

  const start = () => {
    if (timer) clearInterval(timer);
    timer = setInterval(tick, interval);
  };

  section.addEventListener('mouseenter', () => { paused = true; });
  section.addEventListener('mouseleave', () => { paused = false; });
  section.addEventListener('focusin', () => { paused = true; });
  section.addEventListener('focusout', () => { paused = false; });

  // A manual swipe should not fight the timer — restart the countdown once
  // scrolling settles. Debounced, since one smooth scroll fires many events.
  let settle = null;
  track.addEventListener('scroll', () => {
    if (settle) clearTimeout(settle);
    settle = setTimeout(start, 150);
  }, { passive: true });

  start();
}

document.addEventListener('DOMContentLoaded', () => bdMinistryWorkInit());
document.addEventListener('shopify:section:load', (event) => {
  bdMinistryWorkInit(event.target);
});

/* ──────────────────────────────────────────────
   BD Ministry Testimonials — dots-only quote carousel
   Section: sections/bd-ministry-testimonials.liquid

   The track is a focusable scroll-snap container, so arrow keys scroll it
   natively. This adds dot state and autoplay only.

   Wrapping last -> first is an instant jump; a smooth one would visibly
   race through every quote in between.

   Idempotent: binding is guarded by a flag on the section element.
   ────────────────────────────────────────────── */
function bdMinistryTestimonialsInit(root) {
  const scope = root || document;

  scope.querySelectorAll('[data-bmts]').forEach((section) => {
    const track = section.querySelector('[data-bmts-track]');
    if (!track) return;

    const slides = Array.from(section.querySelectorAll('[data-bmts-slide]'));
    const dots = Array.from(section.querySelectorAll('[data-bmts-dot]'));

    if (!slides.length) return;

    const current = () => {
      const step = track.clientWidth;
      if (!step) return 0;
      return Math.min(slides.length - 1, Math.max(0, Math.round(track.scrollLeft / step)));
    };

    const goTo = (index, instant) => {
      const clamped = (index + slides.length) % slides.length;
      track.scrollTo({
        left: clamped * track.clientWidth,
        behavior: instant ? 'auto' : 'smooth',
      });
    };

    const sync = () => {
      const active = current();
      dots.forEach((dot, i) => {
        const on = i === active;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    };

    if (!section._bmtsBound) {
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => goTo(i, false));
      });

      track.addEventListener('scroll', sync, { passive: true });

      // Slide width is the track width, so a resize needs a re-centre.
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => {
          goTo(current(), true);
          sync();
        }).observe(track);
      } else {
        window.addEventListener('resize', sync);
      }

      if (section.dataset.bmtsAutoplay === 'true' && slides.length > 1) {
        bdMinistryTestimonialsAutoplay(section, track, slides, goTo, current);
      }

      section._bmtsBound = true;
    }

    sync();
  });
}

function bdMinistryTestimonialsAutoplay(section, track, slides, goTo, current) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const interval = parseInt(section.dataset.bmtsInterval, 10) || 6000;
  let timer = null;
  let paused = false;

  const tick = () => {
    // Skip while backgrounded, hovered or focused rather than racing ahead.
    if (paused || document.hidden) return;

    const i = current();
    goTo(i + 1, i === slides.length - 1);
  };

  const start = () => {
    if (timer) clearInterval(timer);
    timer = setInterval(tick, interval);
  };

  section.addEventListener('mouseenter', () => { paused = true; });
  section.addEventListener('mouseleave', () => { paused = false; });
  section.addEventListener('focusin', () => { paused = true; });
  section.addEventListener('focusout', () => { paused = false; });

  // Reading a quote should not be interrupted mid-sentence — restart the
  // countdown once a manual scroll settles. Debounced: one smooth scroll
  // fires many events.
  let settle = null;
  track.addEventListener('scroll', () => {
    if (settle) clearTimeout(settle);
    settle = setTimeout(start, 150);
  }, { passive: true });

  start();
}

document.addEventListener('DOMContentLoaded', () => bdMinistryTestimonialsInit());
document.addEventListener('shopify:section:load', (event) => {
  bdMinistryTestimonialsInit(event.target);
});

/* ──────────────────────────────────────────────
   BD Collection Gallery — tab bar arrows
   Section: sections/bd-collection-gallery.liquid

   Separate from bdCollectionGalleryInit() so the existing tab-switching
   logic is untouched. Arrows stay hidden unless the bar actually overflows,
   so a short tab list is unaffected.

   Idempotent: binding is guarded by a flag on the wrapper.
   ────────────────────────────────────────────── */
function bdGalleryTabsScrollInit(root) {
  const scope = root || document;

  scope.querySelectorAll('[data-gallery-tabs-wrap]').forEach((wrap) => {
    const track = wrap.querySelector('[data-gallery-tabs]');
    const prev = wrap.querySelector('[data-gallery-tabs-prev]');
    const next = wrap.querySelector('[data-gallery-tabs-next]');
    if (!track) return;

    const sync = () => {
      // 2px of slack absorbs sub-pixel widths.
      const max = track.scrollWidth - track.clientWidth;
      const overflowing = max > 2;

      [prev, next].forEach((btn) => {
        if (btn) btn.hidden = !overflowing;
      });

      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    };

    // 80% rather than a full width, so the tab at the edge stays visible as
    // an anchor instead of the list jumping to an entirely new set.
    const step = () => track.clientWidth * 0.8;

    if (!wrap._bdgTabsBound) {
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

      track.addEventListener('scroll', sync, { passive: true });

      // Clicking a partly-cut-off tab should bring it fully into view.
      track.querySelectorAll('[data-gallery-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        });
      });

      if ('ResizeObserver' in window) {
        new ResizeObserver(sync).observe(track);
      } else {
        window.addEventListener('resize', sync);
      }

      wrap._bdgTabsBound = true;
    }

    sync();
  });
}

document.addEventListener('DOMContentLoaded', () => bdGalleryTabsScrollInit());
document.addEventListener('shopify:section:load', (event) => {
  bdGalleryTabsScrollInit(event.target);
});

/* ============================================================
   BD USP Marquee — publish the strip's height as a CSS variable.

   custom.css pins the strip at top:0 and offsets the sticky header by
   its height. That height comes from editor settings and shrinks at
   mobile widths, so it has to be measured rather than hard-coded.

   Set on documentElement because the strip and .header-section are
   siblings: a custom property on the strip itself would not inherit
   across to the header.
   ============================================================ */

function bdUspmStickyInit() {
  const strip = document.querySelector('#header-group .bd-usp-marquee-wrapper');
  if (!strip) return;

  const root = document.documentElement;


  /* Flag when the header is pinned, so custom.css can drop the logo row.

     The theme's own data-sticky-state cannot be used: with sticky='always'
     assets/header.js sets it purely from an IntersectionObserver on
     <header-component> at threshold 1, and the navy utility bar sits above
     header-component inside .header-section. Pinned or not, header-component
     lands ~40px down the viewport and stays fully intersecting, so the
     attribute never becomes 'active'. Comparing the section's own rect to
     the offset it is pinned at is unambiguous. */
  const headerSection = document.querySelector('#header-group .header-section');
  if (!headerSection || window.bdUspmScrollBound) return;
  window.bdUspmScrollBound = true;

  // Holds the flow height the hidden row gives up. Outside #header-group
  // on purpose: the theme's header-height script sums that element's
  // children, and a spacer inside would inflate --header-group-height.
  let spacer = document.getElementById('bd-header-spacer');
  if (!spacer) {
    spacer = document.createElement('div');
    spacer.id = 'bd-header-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    document.getElementById('header-group')?.after(spacer);
  }

  const topRow = headerSection.querySelector('.header__row--top');
  const menuRow = headerSection.querySelector('.header__row--bottom');
  const utilityBar = headerSection.querySelector('.header-utility-bar');

  // What the pinned header actually paints, for --sticky-header-offset —
  // the top row's height is always part of that now that custom.css no
  // longer hides it while stuck (it used to be left out here for exactly
  // that reason).
  const measure = () => {
    const painted =
      (utilityBar?.offsetHeight || 0) +
      (topRow?.offsetHeight || 0) +
      (menuRow?.offsetHeight || 0);
    root.style.setProperty('--bd-header-stuck-height', `${painted}px`);
  };

  // rafId (not a queued boolean) for the same reason as
  // bdEbtnStickyInit()'s own sync() below — a queued flag that only
  // ever gets cleared by the rAF callback it's waiting on can get stuck
  // true forever if that one callback is ever dropped (backgrounded tab,
  // etc.), silently discarding every scroll event's sync() from then on.
  // --bd-header-stuck-height set below is exactly what bdEbtnStickyInit()
  // reads as headerOffset, so this one freezing stale would throw that
  // row's own pin/unpin math off too, on top of whatever it breaks here.
  let rafId = null;

  const sync = () => {
    rafId = null;
    measure();

    // Pinned at calc(strip height - 1px), so anything at or above that
    // offset means the section has stopped scrolling with the page. The
    // 2px of hysteresis keeps it from chattering on the exact boundary.
    const wasStuck = root.classList.contains('bd-header-stuck');
    const limit = -0.5;
    const top = headerSection.getBoundingClientRect().top;

    /* The scroll check is what stops a false positive at the very top of
       the page. Dismissing the announcement bar removes its height, which
       leaves .header-section sitting at exactly the offset it pins to —
       so the rect test alone came down to sub-pixel rounding, and when it
       rounded the wrong way the logo row vanished while the page was still
       at scroll zero. Unscrolled is never stuck, whatever the arithmetic
       says. */
    const scrolled = (window.scrollY || document.documentElement.scrollTop || 0) > 1;
    const stuck = scrolled && (wasStuck ? top <= limit + 2 : top <= limit);

    root.classList.toggle('bd-header-stuck', stuck);

    // Collapses the announcement bar and this marquee (custom.css) the
    // moment the page scrolls at all, rather than leaving the marquee
    // pinned at the top forever. Reuses the same `scrolled` flag `stuck`
    // is built from, so both toggle on the same scroll event.
    //
    // The collapse is a max-height transition, not display:none — this
    // strip's ResizeObserver above reports that shrink as it happens, so
    // --bd-uspm-height (and the sticky header offset in custom.css that
    // reads it) drifts down to ~0 over the same transition instead of
    // jumping, and .bd-header-stuck's own `limit` above naturally starts
    // tracking against a collapsing strip rather than a fixed one.
  };

  // rAF-throttled: sync() reads layout, and an unthrottled scroll handler
  // would force a reflow on every event.
  const onScroll = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(sync);
  };

  sync();
  document.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
}


document.addEventListener('DOMContentLoaded', () => bdUspmStickyInit());
document.addEventListener('shopify:section:load', () => bdUspmStickyInit());

/* ============================================================
   BD Event Buttons — pin the "SHOP BY EVENT" row at the top of the
   viewport once its natural position scrolls up to meet the site's own
   sticky header, so it stays reachable for the rest of the page (footer
   included), not just while its own short section is on screen.

   custom.css's .bd-ebtn__inner.is-pinned rule explains why plain CSS
   position: sticky can't do this alone — section.bd-ebtn is only as
   tall as this one row, so sticky would run out of room to stick within
   a few pixels of engaging. Switching to position: fixed here instead,
   toggled by scroll position, keeps it pinned for as long as the
   section itself would otherwise have scrolled past — i.e. permanently,
   once you've scrolled down to it, until you scroll back up above it.
   ============================================================ */

function bdEbtnStickyInit() {
  document.querySelectorAll('.bd-ebtn').forEach((section) => {
    const inner = section.querySelector('.bd-ebtn__inner');

    if (!inner || section.dataset.bdEbtnStickyBound === 'true') return;

    section.dataset.bdEbtnStickyBound = 'true';

    let spacer = section.querySelector('.bd-ebtn__spacer');

    if (!spacer) {
      spacer = document.createElement('div');
      spacer.className = 'bd-ebtn__spacer';
      spacer.setAttribute('aria-hidden', 'true');
      section.appendChild(spacer);
    }

    const eventSections = Array.from(
      document.querySelectorAll('.bd-sbe')
    );

    const lastEventSection =
      eventSections[eventSections.length - 1] || null;

    /*
      Unpin at 70%.

      Once unpinned, don't allow it to immediately
      pin again until the user has moved back
      sufficiently far.
    */
    const UNPIN_PROGRESS = 0.70;
    const REPIN_PROGRESS = 0.62;

    let rafId = null;
    let sectionDocumentTop = 0;

    const getHeaderOffset = () => {
      const rootStyle = getComputedStyle(document.documentElement);

      return (
        parseFloat(
          rootStyle.getPropertyValue('--bd-header-stuck-height')
        ) ||
        parseFloat(
          rootStyle.getPropertyValue('--header-height')
        ) ||
        0
      );
    };

    const getInnerHeight = () => {
      return inner.getBoundingClientRect().height;
    };

    // Read by section.bd-sbe's scroll-margin-top so a jump clears the pinned
    // row as well as the header. Zero when unpinned, so a jump from the top
    // of the page is not pushed down by a row that is not on screen.
    const publishPinnedHeight = () => {
      const px = inner.classList.contains('is-pinned') ? getInnerHeight() : 0;
      document.documentElement.style.setProperty('--bd-ebtn-pinned-height', `${px}px`);
    };

    const applySpacerHeight = () => {
      if (inner.classList.contains('is-pinned')) {
        spacer.style.height = `${getInnerHeight()}px`;
      }
      publishPinnedHeight();
    };

    const getSectionDocumentTop = () => {
      const wasPinned = inner.classList.contains('is-pinned');

      if (wasPinned) {
        inner.classList.remove('is-pinned');
        spacer.style.height = '0px';
      }

      const rect = section.getBoundingClientRect();
      const documentTop = rect.top + window.scrollY;

      if (wasPinned) {
        inner.classList.add('is-pinned');
        spacer.style.height = `${getInnerHeight()}px`;
      }

      publishPinnedHeight();
      return documentTop;
    };

    const getEventProgress = () => {
      if (!lastEventSection) return 0;

      const rect =
        lastEventSection.getBoundingClientRect();

      const height = rect.height;

      if (!height) return 0;

      const headerOffset = getHeaderOffset();

      const coveredHeight = Math.max(
        0,
        Math.min(
          height,
          headerOffset - rect.top
        )
      );

      return coveredHeight / height;
    };

    const unpin = () => {
      if (!inner.classList.contains('is-pinned')) return;

      const height = inner.offsetHeight;

      spacer.style.height = `${height}px`;

      inner.classList.add('is-transitioning');
      inner.classList.remove('is-pinned');

      requestAnimationFrame(() => {
        spacer.style.height = '0px';

        requestAnimationFrame(() => {
          inner.classList.remove('is-transitioning');
        });
      });
    };

    const pin = () => {
      if (inner.classList.contains('is-pinned')) return;

      inner.classList.add('is-transitioning');
      inner.classList.add('is-pinned');

      spacer.style.height = `${getInnerHeight()}px`;

      requestAnimationFrame(() => {
        inner.classList.remove('is-transitioning');
      });
    };

    const sync = () => {
      rafId = null;

      const headerOffset = getHeaderOffset();
      const wasPinned =
        inner.classList.contains('is-pinned');

      const sectionTop =
        section.getBoundingClientRect().top;

      const eventProgress =
        getEventProgress();

      /*
        Normal sticky trigger.
      */
      const reachedStickyPoint =
        sectionTop <= headerOffset;

      /*
        Once we have reached 70%, force the
        element back into normal flow.
      */
      const eventFinished =
        eventProgress >= UNPIN_PROGRESS;

      /*
        When scrolling back up, don't allow the
        element to immediately reappear around
        the 70% boundary.

        It can only pin again after going back
        above 62%.
      */
      const canRepin =
        eventProgress <= REPIN_PROGRESS;

      /*
        User has returned to the original position
        of the section.
      */
      const returnedToDefault =
        window.scrollY <=
        sectionDocumentTop - headerOffset + 2;

      /*
        If currently pinned, ONLY unpin when:
        - 70% threshold is reached
        - or user returns to its default position
      */
      if (wasPinned) {
        if (
          eventFinished ||
          returnedToDefault
        ) {
          unpin();
        } else {
          applySpacerHeight();
        }

        return;
      }

      /*
        If currently unpinned, don't immediately
        re-pin while still inside the 62%-70%
        dead zone.
      */
      if (
        reachedStickyPoint &&
        canRepin &&
        !returnedToDefault
      ) {
        pin();
      }
    };

    const onScroll = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(sync);
    };

    const onResize = () => {
      sectionDocumentTop =
        getSectionDocumentTop();

      onScroll();
    };

    const headerGroup =
      document.getElementById('header-group');

    if (
      headerGroup &&
      typeof ResizeObserver !== 'undefined'
    ) {
      const headerResizeObserver =
        new ResizeObserver(() => {
          sectionDocumentTop =
            getSectionDocumentTop();

          onScroll();
        });

      headerResizeObserver.observe(headerGroup);
    }

    sectionDocumentTop =
      getSectionDocumentTop();

    sync();

    document.addEventListener(
      'scroll',
      onScroll,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      onResize,
      { passive: true }
    );
  });
}

document.addEventListener(
  'DOMContentLoaded',
  bdEbtnStickyInit
);

document.addEventListener(
  'shopify:section:load',
  bdEbtnStickyInit
);

/* ============================================================
   Hulk Volume Discount — refresh after an AJAX cart change.

   The app computes its discounts once, on page load, and paints them into
   [data-hulkapps-line-price] / [data-hulkapps-cart-total]. Horizon changes
   quantities over fetch and re-renders the cart section from the server, so
   those elements come back with the theme's undiscounted figures and the
   app has no idea anything moved — the shopper would see the discount
   vanish the moment they pressed "+".

   So we ask the app's own endpoint again with the updated cart and hand the
   answer to its own painter — no reload, and no second implementation of
   the pricing rules. The request shape is copied from hulkcode.js, which
   posts jQuery-serialised form data to vd_url + /shop/get_cart_details.

   Reload is kept, but only as the fallback: if the endpoint moves, CORS
   blocks it, or the response is not what we expect, a reload still produces
   a correct page. Silently leaving stale prices on screen would be the one
   unacceptable outcome.
   ============================================================ */

/* jQuery's $.param, which is what hulkcode.js uses, so the payload has to
   match its bracket notation: nested objects become a[b][c] and arrays
   a[0][b]. Written out here because the app's jQuery is bundled in
   noConflict and never reaches window. */
   
function bdHulkParam(value, prefix, out) {
  out = out || [];

  if (value === null || value === undefined) {
    if (prefix) out.push(encodeURIComponent(prefix) + '=');
    return out;
  }

  if (Array.isArray(value)) {
    value.forEach((item, i) => bdHulkParam(item, `${prefix}[${i}]`, out));
    return out;
  }

  if (typeof value === 'object') {
    Object.keys(value).forEach((key) => {
      if (typeof value[key] === 'function') return;
      bdHulkParam(value[key], prefix ? `${prefix}[${key}]` : key, out);
    });
    return out;
  }

  out.push(`${encodeURIComponent(prefix)}=${encodeURIComponent(value)}`);
  return out;
}

async function bdHulkRecalculate() {
  const app = window.hulkapps;
  if (!app || !app.vd_url || typeof window.hulkappsDoActions !== 'function') {
    throw new Error('Hulk app not ready');
  }

  const cart = await fetch(`${Theme?.routes?.cart_url || '/cart'}.js`).then((r) => r.json());

  /* Same fields snippets/hulkcode_common.liquid strips before handing the
     cart over — they are large, unused, and blow up the request body. */
  const DROP = [
    'sku', 'grams', 'vendor', 'url', 'image', 'handle',
    'requires_shipping', 'product_type', 'product_description',
  ];
  cart.items.forEach((item) => DROP.forEach((key) => delete item[key]));
  app.cart = cart;

  const body = bdHulkParam({
    cart_data: app,
    store_id: app.store_id,
    // Variants do not change on a quantity edit, so the collection and tag
    // maps built at render time are still accurate.
    cart_collections: JSON.stringify(app.cart_collections || {}),
    cart_product_tags: JSON.stringify(app.product_tags || {}),
    ctags: app.customer?.tags || '',
    currency_rate: window.Shopify?.currency?.rate || 1,
  }).join('&');

  const response = await fetch(`${app.vd_url}/shop/get_cart_details`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  const data = await response.json();
  if (!data?.discounts) throw new Error('No discounts in response');

  window.hulkappsDoActions(data);
}

function bdHulkCartRefreshInit() {
  if (!document.querySelector('[data-hulkapps-cart-total]')) return;
  if (window.bdHulkCartRefreshBound) return;
  window.bdHulkCartRefreshBound = true;

  document.addEventListener('cart:update', (event) => {
    // An add from a product page navigates to the cart on its own.
    if (event.detail?.data?.source === 'product-form-component') return;

    bdShowLoadingOverlay(true);

    bdHulkRecalculate()
      .then(() => bdShowLoadingOverlay(false))
      .catch((err) => {
        console.error('[Hulk] Recalculate failed - reloading instead', err);
        window.location.reload();
      });
  });
}

/* Full-screen veil while the discount is recalculated. The cart's own
   numbers have already been re-rendered by the theme at that point, so
   without it the shopper watches the undiscounted figures sit there. */
function bdShowLoadingOverlay(show) {
  let overlay = document.getElementById('bd-loading-overlay');

  if (!show) {
    overlay?.classList.remove('is-visible');
    return;
  }

  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'bd-loading-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-label', 'Updating your cart');
    overlay.innerHTML = '<span class="bd-loading-overlay__spinner"></span>';
    document.body.appendChild(overlay);
  }

  overlay.classList.add('is-visible');
}

document.addEventListener('DOMContentLoaded', () => bdHulkCartRefreshInit());
document.addEventListener('shopify:section:load', () => bdHulkCartRefreshInit());

/* ============================================================
   Hulk Volume Discount — hold the price behind a skeleton until the
   offer lands.

   The app fetches the cart's discounts after render, so the full price is
   on screen for a beat and then swaps. Rather than show a figure that is
   about to change, custom.css masks it while .bd-hulk-pending is set.

   Cleared on whichever comes first:
     - window.hulkapps.discounts being populated, which happens when the
       response lands whether or not an offer applies — so a single-item
       cart resolves as fast as a discounted one;
     - the app painting .hulkapps-cart-total / .discounted_price, as a
       backstop if it ever stops exposing that object;
     - a hard timeout, so a failed or blocked request shows the real
       prices rather than shimmering forever.
   ============================================================ */
const BD_HULK_SETTLE_TIMEOUT_MS = 5000;

/* How long to wait for the app itself to turn up before concluding it is
   not on this store. Its script is async, so it will not always exist when
   this runs — but a second is plenty, and far better than masking prices
   for the full timeout on a store that has no discount app at all. That is
   exactly what happened on the UK storefront: the theme carries the Hulk
   hooks because it was copied from the US one, nothing ever answered, and
   every cart page shimmered for eight seconds before showing the price. */
const BD_HULK_APP_GRACE_MS = 1000;

function bdHulkPendingInit() {
  if (!document.querySelector('[data-hulkapps-cart-total]')) return;

  const root = document.documentElement;
  if (root.classList.contains('bd-hulk-pending')) return;
  root.classList.add('bd-hulk-pending');

  let observer;
  let poll;
  let bail;
  let appCheck;

  const settle = () => {
    root.classList.remove('bd-hulk-pending');
    clearInterval(poll);
    clearTimeout(bail);
    clearTimeout(appCheck);
    observer?.disconnect();
  };

  // Already answered — a cached response can beat this script.
  if (window.hulkapps?.discounts) return settle();

  // No app on the store: reveal the real prices rather than waiting out a
  // timeout for a response that is never coming.
  appCheck = setTimeout(() => {
    if (!window.hulkapps) settle();
  }, BD_HULK_APP_GRACE_MS);

  poll = setInterval(() => {
    if (window.hulkapps?.discounts) settle();
  }, 100);

  observer = new MutationObserver(() => {
    if (document.querySelector('.hulkapps-cart-total, .discounted_price')) settle();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  bail = setTimeout(settle, BD_HULK_SETTLE_TIMEOUT_MS);
}

document.addEventListener('DOMContentLoaded', () => bdHulkPendingInit());
document.addEventListener('shopify:section:load', () => bdHulkPendingInit());

/* ============================================================
   BD Shop by Event — sections/bd-shop-by-event.liquid

   Event buttons switch the panel below and scroll to it; thumbnails swap
   the large image within a panel.

   Scrolling is left to scrollIntoView with scroll-margin-top set in CSS,
   rather than computing an offset here — the pinned header's height changes
   with viewport width and with whether it is stuck, and CSS already knows
   both through --bd-header-stuck-height.
   ============================================================ */
function bdShopByEventInit(root) {
  const scope = root || document;

  scope.querySelectorAll('.bd-sbe').forEach((section) => {
    if (section.dataset.bsbeBound === 'true') return;
    section.dataset.bsbeBound = 'true';

    const cats = Array.from(section.querySelectorAll('[data-bsbe-cat]'));
    if (!cats.length) return;

    const thumbs = Array.from(section.querySelectorAll('[data-bsbe-thumb]'));

    /* Each category runs its own slider, so its state lives with it rather
       than in one shared index — switching category and coming back keeps
       the banner the visitor was on. */
    /* Marks which banner is current, without moving anything. Split out from
       showSlide because the track can also be dragged, and a drag has to
       update the dots and counter without being told to scroll again — which
       would fight the finger that is still moving it. */
    const markSlide = (cat, next) => {
      cat.dataset.bsbeCurrent = String(next);

      cat.querySelectorAll('[data-bsbe-slide]').forEach((slide) => {
        slide.classList.toggle('is-active', Number(slide.dataset.bsbeSlide) === next);
      });
      cat.querySelectorAll('[data-bsbe-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', Number(dot.dataset.bsbeDot) === next);
      });

      // The "1/5" in the nav pill. Scoped to this category because each one
      // keeps its own position, so the other categories' counters must not
      // move when this one does.
      const counter = cat.querySelector('[data-bsbe-current]');
      if (counter) counter.textContent = String(next + 1);

      syncCta(cat, next);
    };

    /* "View Product" points at whatever banner is currently centred.

       It used to carry the FIRST banner's href from the Liquid and rely on
       pointer-events: none so a click fell through to the banner underneath
       it. That worked while the slides were stacked and the one underneath
       was always the visible one. Now they are a scrolling track, so what
       sits under the button is whatever the scroll position put there —
       often a dimmed neighbour, sometimes the gap between two.

       Setting the href outright is both correct and simpler: the button is a
       real link again, works on keyboard and middle-click, and shows its
       destination in the status bar. */
    const syncCta = (cat, index) => {
      const cta = section.querySelector('.bd-sbe__cta');
      if (!cta) return;

      const slide = cat.querySelector('[data-bsbe-slide="' + index + '"]');
      const link = slide && slide.querySelector('.bd-sbe__slide-link');

      // A banner with no link of its own leaves the section's own CTA link in
      // place rather than pointing nowhere.
      if (link && link.getAttribute('href')) cta.setAttribute('href', link.getAttribute('href'));
    };

    const showSlide = (cat, index) => {
      const slides = Array.from(cat.querySelectorAll('[data-bsbe-slide]'));
      if (!slides.length) return;

      // Wrap, so the arrows never dead-end at either edge.
      const next = (index + slides.length) % slides.length;
      markSlide(cat, next);

      /* Centre it in the track. scrollIntoView would do this in one line but
         also scrolls the PAGE to bring the track into view, which hijacks the
         viewport when someone clicks an arrow on a banner already on screen.
         Setting scrollLeft moves only the track. */
      const track = cat.querySelector('[data-bsbe-track]');
      const slide = slides[next];
      if (track && slide) {
        track.scrollTo({
          left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
          behavior: 'smooth',
        });
      }
    };

    cats.forEach((cat) => {
      cat.dataset.bsbeCurrent = '0';
      const current = () => Number(cat.dataset.bsbeCurrent || 0);

      cat.querySelector('[data-bsbe-prev]')?.addEventListener('click', () => showSlide(cat, current() - 1));
      cat.querySelector('[data-bsbe-next]')?.addEventListener('click', () => showSlide(cat, current() + 1));
      cat.querySelectorAll('[data-bsbe-dot]').forEach((dot) => {
        dot.addEventListener('click', () => showSlide(cat, Number(dot.dataset.bsbeDot)));
      });

      /* A track can be dragged, flicked or wheeled, none of which go through
         showSlide — so without this the dots and the counter would sit on
         whatever the arrows last said while the visitor looked at a different
         banner. Whichever slide is nearest the centre line wins. */
      const track = cat.querySelector('[data-bsbe-track]');
      if (!track) return;

      let queued = false;
      track.addEventListener(
        'scroll',
        () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => {
            queued = false;
            const slides = Array.from(cat.querySelectorAll('[data-bsbe-slide]'));
            if (!slides.length) return;

            const centre = track.scrollLeft + track.clientWidth / 2;
            let best = 0;
            let bestDistance = Infinity;
            slides.forEach((slide, i) => {
              const distance = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - centre);
              if (distance < bestDistance) {
                bestDistance = distance;
                best = i;
              }
            });

            if (String(best) !== cat.dataset.bsbeCurrent) markSlide(cat, best);
          });
        },
        { passive: true }
      );
    });

    const showCategory = (index) => {
      cats.forEach((cat) => {
        const on = Number(cat.dataset.bsbeCat) === index;
        cat.classList.toggle('is-active', on);
        // Hidden categories leave the accessibility tree as well as the view.
        if (on) cat.removeAttribute('aria-hidden');
        else cat.setAttribute('aria-hidden', 'true');
      });
      thumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', Number(thumb.dataset.bsbeThumb) === index);
      });

      /* Each category remembers its own position, so switching to one has to
         pick up ITS current banner — not reset to the first, and not leave
         the button pointing into the category the visitor just left. */
      const active = cats.find((cat) => Number(cat.dataset.bsbeCat) === index);
      if (active) syncCta(active, Number(active.dataset.bsbeCurrent || 0));
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => showCategory(Number(thumb.dataset.bsbeThumb)));
    });

    /* Published for sections/bd-event-buttons.liquid. One event per section
       now, so the button only has to scroll here — there is no panel to
       switch. Kept on the same name so the buttons need no change. */
    /* Waits for the smooth scroll to stop, then corrects.

       scrollIntoView works out where to land from the scroll-margin-top as
       it is at the moment of the click. On the FIRST jump from the top of
       the page that is wrong by the height of the event-buttons row: the row
       is not pinned yet, so --bd-ebtn-pinned-height is 0 — and then the row
       pins DURING the scroll and covers the top of what you just scrolled
       to. Every jump after that looked fine because the row was already
       pinned and the numbers agreed.

       Predicting the pinned height while unpinned is not really possible —
       the row is two lines of pills in flow and one compact scrolling line
       when pinned, so its own height is not the answer either. Re-measuring
       once everything has settled sidesteps the whole problem, and also
       absorbs the site header collapsing mid-scroll. */
    const settleTo = (el) => {
      let last = -1;
      let still = 0;

      const tick = () => {
        const y = window.scrollY;
        still = Math.abs(y - last) < 1 ? still + 1 : 0;
        last = y;

        // Three quiet frames: one is not enough, smooth scrolling stalls.
        if (still < 3) {
          requestAnimationFrame(tick);
          return;
        }

        // Resolved value, so it already includes whatever the pinned row and
        // the header ended up being.
        const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
        const delta = el.getBoundingClientRect().top - margin;

        // 2px of slack: correcting sub-pixel rounding would just start
        // another scroll, and another frame of this.
        if (Math.abs(delta) > 2) {
          window.scrollTo({ top: window.scrollY + delta, behavior: 'smooth' });
        }
      };

      requestAnimationFrame(tick);
    };

    section.bdSbeShowByKey = (key) => {
      if (section.dataset.bdEvent !== key) return false;
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      settleTo(section);
      return true;
    };
  });
}

document.addEventListener('DOMContentLoaded', () => bdShopByEventInit());
document.addEventListener('shopify:section:load', (event) => bdShopByEventInit(event.target));

/* Buttons from sections/bd-event-buttons.liquid, which sits above the
   gallery as its own section. Delegated from document because the two
   sections initialise independently and either can be re-rendered on its
   own by the theme editor, which would drop a directly-bound listener. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-bsbe-jump]');
  if (!trigger) return;

  const key = trigger.dataset.bsbeJump;
  const handled = Array.from(document.querySelectorAll('.bd-sbe')).some(
    (section) => typeof section.bdSbeShowByKey === 'function' && section.bdSbeShowByKey(key)
  );

  /* Pinned, the row is a single horizontal scroller (see
     .bd-ebtn__inner.is-pinned in custom.css), so the button just pressed can
     be half off the edge — and after the page jumps, the visitor has no
     landmark for where they are in the list. Centring it keeps the pressed
     event and its neighbours in view. inline only: block: 'nearest' so this
     never fights the vertical jump that just started. */
  const scroller = trigger.closest('.bd-ebtn__inner.is-pinned .bd-ebtn__buttons');
  if (scroller) {
    trigger.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }

  if (!handled) {
    console.warn(
      `[SBE] No gallery event matches "${key}" - the event names must match in both sections`
    );
  }
});

/* ============================================================
   BD Guarantee Tabs — "We've Got You, Every Step of the Way."
   Section: sections/bd-guarantee-tabs.liquid

   Plain show/hide, same data-attribute + .is-active pattern as
   bd-shop-by-event's category thumbs — click a tab, show its panel
   (already-hidden via the [hidden] attribute server-side on every
   panel but the first, so this works with JS disabled too).

   Idempotent: guarded by a flag on the section, so theme-editor
   reloads (shopify:section:load) never double-bind.
   ============================================================ */
function bdGuaranteeTabsInit(root) {
  const scope = root || document;

  scope.querySelectorAll('.bd-gtab').forEach((section) => {
    if (section.dataset.bdGtabBound === 'true') return;
    section.dataset.bdGtabBound = 'true';

    const tabs = Array.from(section.querySelectorAll('[data-gtab-tab]'));
    const panels = Array.from(section.querySelectorAll('[data-gtab-panel]'));
    if (!tabs.length || !panels.length) return;

    const showTab = (index) => {
      tabs.forEach((tab) => {
        const on = tab.dataset.gtabTab === String(index);
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      panels.forEach((panel) => {
        const on = panel.dataset.gtabPanel === String(index);
        panel.classList.toggle('is-active', on);
        panel.hidden = !on;
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => showTab(tab.dataset.gtabTab));
    });
  });
}

document.addEventListener('DOMContentLoaded', () => bdGuaranteeTabsInit());
document.addEventListener('shopify:section:load', (event) => bdGuaranteeTabsInit(event.target));


/* ──────────────────────────────────────────────
   Church products -> church forms, applied at runtime.

   sections/product-design-main.liquid already does this properly, with
   {% if is_church_product %} on both buttons. That code is in the file — it
   is visible in the admin code editor and in a fresh CLI pull — but the
   storefront keeps rendering the pre-edit markup. A fresh render (no page
   cache, render;dur reported by Shopify) still emits data-custom-quote, so
   the compiled copy of that one section is stuck server-side.

   Rather than leave church enquiries going to proofs@ and the Mockup tab
   while that resolves, the swap is done here instead. custom.js recompiles
   normally, so this takes effect immediately.

   Idempotent and self-cancelling: it only rewrites buttons that still carry
   the general attribute, so when the section does start compiling again this
   finds nothing to do and can simply be deleted.
   ────────────────────────────────────────────── */
function bdChurchFormRoutingInit() {
  /* Read the tags Shopify already prints into the page rather than fetching
     /products/<handle>.js — the data is right there, and a product page that
     has to make a network call before its buttons work correctly is a race
     waiting to be lost. */
  const readTags = () => {
    const el = document.querySelector('[data-variant-json], [data-pfab-variants], script[type="application/json"]');
    const html = document.documentElement.innerHTML;
    const match = html.match(/"tags":\s*\[([^\]]*)\]/);
    return match ? match[1].toLowerCase() : '';
  };

  const tags = readTags();
  if (tags.indexOf('family:church') === -1) return;

  const swap = [
    ['data-mockup-quote', 'data-church-mockup'],
    ['data-custom-quote', 'data-church-custom-quote'],
  ];

  swap.forEach(([general, church]) => {
    document.querySelectorAll('[' + general + ']').forEach((el) => {
      // The popups themselves listen for these; only rewrite real buttons.
      if (el.closest('.chmrp-modal, .chcsp-modal, .mrp-modal, .csp-modal')) return;
      el.removeAttribute(general);
      el.setAttribute(church, '');
    });
  });
}

document.addEventListener('DOMContentLoaded', () => bdChurchFormRoutingInit());
document.addEventListener('shopify:section:load', () => bdChurchFormRoutingInit());
